import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { videoBeatCostFor } from '@/store/slices/aiCredits';
import {
  cancelJob,
  enqueueGeneration,
  refineStoryboard,
  selectCreativeJobsMap,
  SINGLE_CLIP_UNSUPPORTED,
  type ShotPlan,
  type StoryboardPipeline,
  type StoryboardPromotion,
  type StoryboardBeatInput,
  type VideoAspectRatio,
} from '@/store/slices/creativeJobs';
import { fetchOwnerWallets } from '@/store/slices/wallet';
import {
  clearStoredStoryboard,
  loadStoredStoryboard,
  storeStoryboard,
  storyboardStorageKey,
} from '../../storyboard-storage';

import { Button, Icon, Text, TextArea, type SelectOption } from '@/nova/components';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { useFeatureGate } from '@/nova/hooks/useFeatureGate';
import { GenerateButton } from '../GenerateButton';
import { ComposerRailTabs, useRailEnter, type ComposerRailTab } from '../ComposerRailTabs';
import { TrackingLink } from '../TrackingLink';
import { adSubjectFromItem } from '../../ad-subjects';
import type { AdSubject } from '../../banner-kit-api';
import { PoolPickerSheet } from '../PoolPickerSheet';
import { GamePickerSheet } from '../GamePickerSheet';
import { GamificationPickerSheet } from '../GamificationPickerSheet';
import { AssetStatsSheet, StatsPill } from '../AssetStats';
import { PromotionsField } from '../PromotionsField';
import { AdLanguageSection } from '../AdLanguageSection';
import { adLanguageLabel, type LocalizedCopy } from '../../ad-language';
import { useSiteLanguages } from '../../hooks/useSiteLanguages';
import { useLocalizedPromotions } from './useLocalizedPromotions';
import { subjectCta, type VideoSubjectType } from '../../video-subjects';
import { storyboardPromotionFromGame, type GameSourceRecord } from '../../game-sources';
import { storyboardPromotionFromFeature, type GamificationFeature } from '../../gamification-sources';
import { uploadArtwork, bestFormatUrlForAspect, type CreativeItem } from '../../creative-api';
import { MoodBoardField } from './MoodBoardField';
import { NewVersionLink, useLockedNudge } from '../NewVersionLink';
import { ArtStylePicker } from '../../../art-style/components/ArtStylePicker';
import { IngredientSection, SectionHint } from '../../../art-style/components/IngredientSection';
import { MascotPicker } from '../../../art-style/components/MascotPicker';
import { AdArtStyleSection, AdCharacterSection, useAdStyleRun } from '../AdArtStyle';
import {
  appendFinishedAdVersion,
  draftFromStoryboard,
  singleClipSetupOf,
  storyboardToSaved,
  upsertVideoDraft,
  versionKey,
  type SavedStoryboard,
  type SavedVideo,
} from '../../video-library-api';
import { useImmersive } from '@/nova/layouts/MainLayout/immersiveControl';
import { assetFilename, downloadAsset } from '../../download-asset';
import { isVideoAspect, keyArtVersionId, type Translate } from '../../video-composer';
import {
  maxSubjectsForLength,
  STORYBOARD_PLACEMENT,
  STORYBOARD_TEMPLATES,
  activityPhrases,
  buildVibeBrief,
  canonicalShotPlan,
  compileKeyOf,
  draftIsReady,
  ensureDistribution,
  isCancelled,
  isContentPolicyFailure,
  isTimeoutFailure,
  keyArtOf,
  localizePromotionsCopy,
  promotionFromItem,
  promotionKey,
  promotionsCtaIn,
  providerRejectionReason,
  newestJobOfKind,
  preStartPhrases,
  seedDraft,
  setPromotions,
  shotPlanProblem,
  singleClipActivityPhrases,
  singleClipStageCopy,
  snapDraftDurations,
  formatVersionTimestamp,
  stageCopy,
  storyboardCost,
  templateById,
  toStoryboardInput,
  type StoryboardDraft,
  type StoryboardTemplateId,
} from '../../storyboard';
import {
  VIDEO_MODELS,
  DEFAULT_VIDEO_MODEL,
  SINGLE_CLIP_UNITS,
  clampClipLength,
  clipLengthsForModel,
  durationsForModel,
  estimatedClipMinutes,
  estimatedMinutes,
  isApproxClipLength,
  runProgressPct,
  runUnitMs,
  selectableVideoModels,
  singleClipCost,
  DEFAULT_V1_TARGET_SECONDS,
} from '../../video-models';
import { aspectsForModel, aspectLabel, aspectRatioValue, snapAspectToModel } from '../../aspect-ratios';
import { videoViewFromPath } from '../../video-routes';
import { STATS_SHEET_PARAM, useSheetParam } from '../../useSheetParam';
import { STORYBOARD_COMPOSE, STORYBOARD_REVEAL } from '@/nova/theme/tokens';
import { StoryboardCanvas } from './StoryboardCanvas';
import { BeatModal } from './BeatModal';
import { EasyFootSelect } from './EasyFootSelect';
import { RunStatusLine } from './RunStatusLine';
import { MatureVideoChip } from './MatureVideoChip';
import { ModelOptionMeta } from './ModelOptionMeta';
import { StudioSelect } from './StudioSelect';
import { ShotListEditor } from './ShotListEditor';
import { FinalPromptPanel, type KeyedCompiledPrompt } from './FinalPromptPanel';
import { DotField } from '../../../customizer/components/DotField';
import { BeatFilmstrip } from './BeatFilmstrip';
import { VideoReveal } from './VideoReveal';
import { LumioGlyph, LumioMark } from '../LumioMark';
import {
  BarField,
  BottomBar,
  DeployProgressFill,
  DeployProgressTrack,
  EasyBeatDot,
  EasyBeatError,
  EasyRetryBtn,
  EasyBeatMotion,
  EasyBeatRole,
  EasyBeatRow,
  EasyBeatSkelBars,
  EasyBeatSkelDot,
  EasyBeatSkelRole,
  EasyBeatSkelRow,
  EasyBeatSkelText,
  EasyBeatText,
  EasyBeats,
  EasyBeatsHead,
  EasyBeatsSection,
  EasyStaleNote,
  EasyBtnGlyph,
  EasyGuide,
  EasyGuideIcon,
  EasyGuideStep,
  EasyGuideStepNum,
  EasyGuideStepText,
  EasyGuideSteps,
  EasyGuideText,
  EasyGuideTitle,
  EasyHeader,
  EasyHeaderLeft,
  EasyNotice,
  EasyLockedFoot,
  EasyFootFacts,
  EasyFootFact,
  EasyFootFactDot,
  EasyOutput,
  EasyOutputEmpty,
  EasyOutputStage,
  EasyOutputFoot,
  EasyFootChip,
  EasyOutputHint,
  EasyOutputScroll,
  EasyPromptInput,
  EasyRail,
  EasyRailFoot,
  EasyRailNote,
  EasyRailNoteText,
  EasyRailScroll,
  EasyRunFoot,
  EasyRunFootRow,
  EasySplit,
  EasyStoryboardBtn,
  EasyStoryboardBtnText,
  EasyTitleInput,
  EasyTitleEditIcon,
  EasyTitleWrap,
  EasyVideo,
  EmptyText,
  Footer,
  HintText,
  LeftControls,
  MascotRow,
  Root,
  StatusBlock,
  Toolbar,
  FailText,
} from './StoryboardComposer.styles';
import { CanvasGlowAngleProperty } from './nodes/nodes.styles';
import { beatRunStatus } from './graph';

export type StoryboardComposerProps = {
  tenantId?: number;
  brandName: string;
  brandDomain?: string;
  brandLogoUrl?: string;
  /** Promotions whose key art can seed a storyboard (the brand's banner pool). */
  items: CreativeItem[];
  /** Per-beat credit cost (undefined until the price table loads; also gates the section upstream). */
  beatCost?: number;
  /** Desktop immersive editor: the canvas goes full-viewport and the toolbar/footer float as pills over it.
   *  False on mobile (the composer keeps its normal-flow stacked column). */
  immersed?: boolean;
  /** Reopen a saved video ad into the editor: its persisted params rebuild the full editable draft on mount
   *  (Workstream B). Undefined for a fresh "create new". */
  reopen?: SavedVideo;
  /** What this ad advertises, from the caller (authoritative for the whole session). Falls back to the
   *  reopened ad's persisted subject_type, then promotion. Drives the setup card's subject-aware labels. */
  subjectType?: VideoSubjectType;
  /** The pipeline this ad renders with, resolved by the library: a NEW ad takes its video tab's (Video Ads =
   *  `beats`, Video Ads v2.0 = `single_clip`), a reopened ad keeps its saved one. Fixed for the editor's life: there
   *  is no in-editor switch, and every version of an ad shares its pipeline. */
  pipeline: StoryboardPipeline;
  /** Presentation. 'easy' (default) renders a MODAL: a setup rail + read-only beats on the left, the output
   *  player + Generate on the right. 'advanced' renders the immersive node canvas. Both share this component's
   *  state, keyed by the same /videos/:id URL; a mode toggle just re-renders the other presentation. */
  mode?: 'easy' | 'advanced';
  /** Easy modal: leave the editor back to the library (the Sheet's close / backdrop / Escape). */
  onClose?: () => void;
  /** Flip between easy and advanced presentations (the header toggle; drives `?mode`). */
  onToggleMode?: () => void;
  onBuyCredits: (needed?: number) => void;
  onNotify: (message: string) => void;
  /** Optimistically merge an in-editor edit (name / storyboard / job_id) into the library's cached list, so a reopen
   *  (the editor remounts when switching easy<->advanced) or the listing card reads the fresh value at once,
   *  instead of the pre-edit entity while the debounced server write is still in flight. */
  onEntityPatch?: (id: string, fields: Partial<SavedVideo>) => void;
  /** The brand's primary art style is flagged mature (the library's read). The composer decides from the art style
   *  the owner PICKS for the video (video models refuse adult content, so a mature pick blocks every render: generate,
   *  regenerate from a beat, retry; composing stays open); this stands in only until the pick is read. */
  matureStyle?: boolean;
  /** Unused: the composer keeps renders blocked while its own read of the picked style is in flight (fails closed).
   *  Kept so the library's props still type-check. */
  styleChecking?: boolean;
};

const DEFAULT_TEMPLATE: StoryboardTemplateId = 'big-win-hype';

/** How many mood-board reference images the owner may attach (mirrors the banner references cap). */
const MAX_MOOD_IMAGES = 4;

/** "Redraw copy" (a copy-only redo: every beat reused, only the animated copy redrawn) is a LOCAL tool for now: shown
 *  only with the "777devdebug" code and by a dev build talking to a local spark-creatives behind the dev proxy, the only backend that takes a rerun from
 *  beat N+1 so far. Statically false in every deployed build. */
const LOCAL_COPY_REDO = import.meta.env.DEV && Boolean(import.meta.env.VITE_CREATIVES_PROXY_TARGET);

/** The easy rail's setup sections (the Art Style page's accordion, as the image banner editor's rail). */
type EasyRailSection = 'subject' | 'language' | 'vibe' | 'length' | 'prompt' | 'mood' | 'art-style' | 'character';

// Placeholder ad names the create/seed flow assigns (the promotion/bare path stores the raw English "Untitled
// video ad"; multi-game/feature use t('Game ad')/t('Gamification ad')). A compose-suggested title replaces the
// name ONLY when it still matches one of these (or is empty), so an owner-typed name is never overwritten.
const DEFAULT_AD_NAME_KEYS = ['Untitled video ad', 'Game ad', 'Gamification ad'];

/** Read a picked/dropped/pasted image File into a data URI (uploaded to CDN before it enters the draft). */
const readAsDataUri = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });

/** A signature of the inputs a storyboard's beats were composed from: the promotion set (order matters, F0 is
 *  the key visual), the vibe/template, and the free-text prompt. Comparing the live inputs to the signature
 *  captured at compose time tells the easy editor its on-screen beats are stale and need regenerating. */
/** Target-length options for the setup rail (seconds). Drives the beat count: on Generate storyboard the
 *  refine LLM fits the per-beat 4/6/8s durations near the chosen total. Labels run through t(). */
const DURATION_TARGET_OPTIONS: { value: string; label: string }[] = [
  { value: '10', label: '10s' },
  { value: '15', label: '15s' },
  { value: '20', label: '20s' },
  { value: '30', label: '30s' },
];

const storyboardSignature = (
  promotions: StoryboardDraft['promotions'],
  templateId: string,
  prompt: string,
  moodImages: string[],
  targetDurationSeconds?: number,
  pipeline: StoryboardPipeline = 'beats',
  videoModel?: string,
  language?: string,
): string =>
  JSON.stringify({
    p: promotions.map((x) => x.banner_id),
    t: templateId,
    pr: prompt.trim(),
    m: moodImages,
    d: targetDurationSeconds ?? 0,
    // Single clip: the pipeline and the video model are part of the setup the plan was composed for, so changing
    // the model marks it stale (spec 7). A v1 signature is exactly what it was before.
    ...(pipeline === 'single_clip' ? { pl: pipeline, vm: videoModel ?? '' } : {}),
    // The ad's language: the compose wrote the call to action (and quotes the copy) in it.
    ...(language ? { l: language } : {}),
  });

/** Canonical, round-trip-stable serialization of a beat list for equality (fixed field order, normalized
 *  optionals), so beats that came back through the backend still compare equal to the live draft's. */
const canonicalBeats = (beats: StoryboardBeatInput[]): string =>
  JSON.stringify(
    beats.map((b) => [
      b.role,
      b.duration_seconds,
      (b.motion_intent ?? '').trim(),
      b.end_frame.source,
      b.end_frame.source === 'generate' ? b.end_frame.prompt : '',
      b.end_frame.source === 'url' ? b.end_frame.url : '',
      b.promotion_index ?? 0,
    ]),
  );

/** Whether the live draft still matches the storyboard CONTENT a version was saved with (setup + beats + CTA),
 *  ignoring render-only knobs (video model, aspect ratio) which the backend rewrites onto the version when it
 *  renders. True => a Generate FILLS that version in place; false (the storyboard was edited) => a new version.
 *  Both sides are normalized through `draftFromStoryboard` so a backend round-trip can't spuriously differ.
 *  The v1 music brief is deliberately NOT compared: it is compose output with no UI, so only a recompose changes
 *  it, and every recompose lands (and selects) its own new version, so it can never tell an unchanged draft from
 *  its version. Comparing it would only let a round-trip that drops the key (a row saved before the field
 *  existed, or a backend that does not persist it yet) turn every compose -> Generate into a new version. */
const draftMatchesVersion = (
  draft: StoryboardDraft,
  templateId: string,
  prompt: string,
  saved: SavedStoryboard,
  fallbackImageUrl: string,
): boolean => {
  const s = draftFromStoryboard(saved, fallbackImageUrl);
  return (
    storyboardSignature(draft.promotions, templateId, prompt, draft.moodImages, draft.targetDurationSeconds) ===
      storyboardSignature(
        s.draft.promotions,
        s.templateId ?? templateId,
        s.prompt,
        s.draft.moodImages,
        s.draft.targetDurationSeconds,
      ) &&
    canonicalBeats(draft.beats) === canonicalBeats(s.draft.beats) &&
    (draft.cta ?? '').trim() === (s.draft.cta ?? '').trim() &&
    // A version composed in another language is not this draft's (its copy and call to action are in that one).
    (draft.language ?? '').trim() === (s.draft.language ?? '').trim() &&
    // The pipeline + the (owner-editable) shot plan are storyboard content too; the model stays a render knob.
    draft.pipeline === s.draft.pipeline &&
    canonicalShotPlan(draft.shotPlan) === canonicalShotPlan(s.draft.shotPlan)
  );
};

/** Overlay `next` onto `prev` by index, keeping a `prev` value wherever `next` has none (empty/absent).
 *  Used to hold the previous run's frames/clips on screen while a regenerate/resume's new job fills in. */
const mergeByIndex = (next?: string[], prev?: string[]): string[] | undefined => {
  if (!next && !prev) return undefined;
  const out = [...(prev ?? [])];
  (next ?? []).forEach((v, i) => {
    if (v) out[i] = v;
  });
  return out;
};

/** The simulated type-in reveal after a compose returns: `step` is the 0-based beat whose motion prose is
 *  currently typing in (earlier beats done, later ones still skeletons), `typed` is how many of its chars
 *  have been revealed. Null when no reveal is running. The reveal effect owns this end-to-end. */
type ComposeReveal = { step: number; typed: number };

/** Whether the viewer asked for reduced motion, so the type-in is skipped (refined beats appear at once)
 *  and the shimmer/caret animations are off (also handled in CSS via the REDUCED_MOTION media query). */
const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Storyboard composer: turns a promotion's key art into a multi-beat, branded video ad. The owner
 * picks a promotion (seeds the draft from its copy) and a template, tunes the beats on a node canvas,
 * and generates. Generation is async (like the video composer): enqueuing charges credits and returns
 * a job id tracked in the durable creative-jobs slice, so it survives a reload (recovered via
 * {@link newestJobOfKind}). Progress arrives as `creative_job` websocket pushes; the app-wide
 * useCreativeJobsStream re-reads the jobs list when they stall (and polls only for a local spark). The finished ad
 * plays inline via {@link VideoResult}.
 */
export const StoryboardComposer = ({
  tenantId,
  brandName,
  brandDomain,
  brandLogoUrl,
  items,
  beatCost,
  immersed,
  reopen,
  subjectType: subjectTypeProp,
  pipeline,
  mode = 'advanced',
  onClose,
  onToggleMode,
  onBuyCredits,
  onNotify,
  onEntityPatch,
  matureStyle: matureActiveStyle = false,
}: StoryboardComposerProps): React.ReactElement => {
  // Raw tools (the copy-only redo) show only with the "777devdebug" code (`creatives-dev-debug`).
  const devDebug = useFeatureGate().isEnabled('creatives-dev-debug');
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  // A plain (key, options) => string translator for the pure seeding helpers (idempotent wrapper of t).
  const translate = React.useCallback<Translate>((key, options) => t(key, options), [t]);
  // An ENGLISH translator for the single-clip vibe brief: the compose LLM gets it in English whatever the
  // owner's language (all shot-plan text is English, spec 4.1).
  const translateEn = React.useCallback<Translate>((key, options) => i18n.getFixedT('en')(key, options), [i18n]);
  // The English vibe brief (the template's title) sent with a single-clip compose / generate and persisted with
  // the draft; undefined for a v1 draft.
  const vibeOf = React.useCallback(
    (d: StoryboardDraft, tpl: string): string | undefined =>
      d.pipeline === 'single_clip' ? buildVibeBrief(templateById(tpl), translateEn) : undefined,
    [translateEn],
  );

  const firstItem = items[0];

  // What this ad advertises, from the (persisted) draft subject_type; drives the setup card's field label,
  // the pool-picker title, the chip fallback and the empty state, so a Games / Gamification ad never reads
  // "Promotions". Defaults to promotion for legacy ads with no subject_type.
  const subjectType: VideoSubjectType = subjectTypeProp ?? reopen?.storyboard?.subject_type ?? 'promotion';
  const subjectFieldLabel =
    subjectType === 'game' ? t('Games') : subjectType === 'gamification' ? t('Gamification features') : t('Promotions');
  const subjectChipLabel =
    subjectType === 'game' ? t('Game') : subjectType === 'gamification' ? t('Feature') : t('Promotion');
  const subjectPickerTitle =
    subjectType === 'game'
      ? t('Choose the games this ad advertises')
      : subjectType === 'gamification'
        ? t('Choose the gamification features this ad advertises')
        : t('Choose the promotions this ad advertises');

  const [templateId, setTemplateId] = React.useState<StoryboardTemplateId>(DEFAULT_TEMPLATE);
  // The video model the beats are animated with (owner-selectable). Generation-time only, so it lives
  // beside the draft rather than in it; passed through to the backend, which falls back to its default. A reopened
  // single clip starts on its SAVED model (an ad saved before its first compose has no plan to hydrate it from).
  const [videoModel, setVideoModel] = React.useState<string>(() =>
    reopen && pipeline === 'single_clip' ? singleClipSetupOf(reopen.storyboard).videoModel : DEFAULT_VIDEO_MODEL,
  );
  // The easy rail's setup sections: one open at a time (the subject first); clicking the open one closes it.
  const [openRailSection, setOpenRailSection] = React.useState<EasyRailSection | null>('subject');
  // On a phone the two rails are tabs (Ingredients / Video); composing a storyboard from Ingredients shows Video. Null
  // until the owner picks one: the tab then follows the ad (see `railTab` below).
  const [pickedRailTab, setRailTab] = React.useState<ComposerRailTab | null>(null);
  // The art style the video is drawn in (the primary one unless the owner picks another) and its character (Auto by
  // default): run options, never saved with the ad. The picked style's cast is what the Character pickers offer, and
  // whether IT is mature decides whether video renders are possible.
  const styleRun = useAdStyleRun(tenantId);
  // Read by the compose (runRefine) without re-creating it on every pick.
  const styleIdRef = React.useRef(styleRun.sendStyleId);
  styleIdRef.current = styleRun.sendStyleId;
  const styleLoadedRef = React.useRef(styleRun.loaded);
  styleLoadedRef.current = styleRun.loaded;
  // Video models refuse adult content, so a mature PICKED style blocks every render. Until the pick is known the
  // library's read of the primary style stands in, and renders stay blocked (fails closed: `styleChecking`); the
  // mature note shows only for a confirmed mature style.
  const matureStyle = styleRun.loaded ? styleRun.mature : matureActiveStyle;
  const styleChecking = !styleRun.loaded;
  // Free-text prompt (optional) that steers the LLM compose: fed to refineStoryboard's required `prompt`
  // (which otherwise just carries the template title). Compose-time only, so it lives beside the draft and
  // is read via a ref in runRefine (typing it must not re-create the callback). Not persisted across reload.
  const [userPrompt, setUserPrompt] = React.useState('');
  // Which of the picked style's characters the video draws (Auto by default; the picker hides without a cast). Not
  // saved with the ad: it is a choice for the next render, made for the style it was picked in.
  const mascotPick = styleRun.mascotPick;
  const setMascotPick = styleRun.pickMascot;
  // The entity's name (Video Ads library entry), edited via the top bar name input (title-editor effect below).
  const [name, setName] = React.useState(reopen?.name ?? '');
  // The easy header's title input, so its pencil affordance can focus it (mirrors the immersive TopBar).
  const easyNameRef = React.useRef<HTMLInputElement>(null);
  const [draft, setDraft] = React.useState<StoryboardDraft>(() => {
    // Entity flow (Video Ads library): start with NO beats. A fresh "Create new" then stays in the setup
    // phase (setup card + key visual, no beat cards) until the owner clicks "Generate storyboard" (the
    // create -> setup -> generate -> typewriter flow). A reopened ad WITH beats is hydrated by the layout
    // effect below (this empty seed is replaced before paint); the saved aspect is carried so a portrait ad
    // doesn't flash landscape first. Only the legacy, entity-less path seeds the base template up front.
    if (reopen) {
      // Restore the ad's SAVED subjects (promotions / games / gamification features) so a reopened ad keeps
      // what it advertises even before any storyboard was generated. Only a brand-new draft with none saved
      // falls back to preselecting the first pool item (the promotion setup default). A reopen WITH beats is
      // replaced by the hydration effect below regardless.
      const savedPromotions = Array.isArray(reopen.storyboard?.promotions) ? reopen.storyboard.promotions : [];
      // A brand-new ad starts with NO promotion: the owner picks one in the promotions sheet (opened on mount below).
      const preset = savedPromotions;
      // The pipeline comes from the library (the tab's for a new ad, the saved one for a reopened ad).
      return {
        aspect: reopen.storyboard?.aspect_ratio === '9:16' ? '9:16' : '16:9',
        cta: preset[0]?.cta_text ?? '',
        promotions: preset,
        beats: [],
        // Restore any saved mood board straight away (the full hydration effect also sets it via draftFromStoryboard).
        moodImages: Array.isArray(reopen.storyboard?.mood_images) ? reopen.storyboard.mood_images : [],
        // The saved language ('' for an ad made before the field, or minted before the website's languages were
        // read: the automatic fill below gives an uncomposed one the website's default).
        language: typeof reopen.storyboard?.language === 'string' ? reopen.storyboard.language.trim() : '',
        pipeline,
        // A single clip always carries a legal length for its (saved, else default) model, so the Target length
        // never reads blank and an uncomposed ad keeps the owner's saved length.
        ...(pipeline === 'single_clip'
          ? { targetDurationSeconds: singleClipSetupOf(reopen.storyboard).targetDurationSeconds }
          : { targetDurationSeconds: reopen.storyboard?.target_duration_seconds || DEFAULT_V1_TARGET_SECONDS }),
      };
    }
    return firstItem
      ? seedDraft(
          templateById(DEFAULT_TEMPLATE),
          [promotionFromItem(firstItem)],
          brandName,
          '16:9',
          translate,
          DEFAULT_V1_TARGET_SECONDS,
        )
      : {
          aspect: '16:9',
          cta: '',
          promotions: [],
          beats: [],
          moodImages: [],
          pipeline: 'beats',
          targetDurationSeconds: DEFAULT_V1_TARGET_SECONDS,
        };
  });
  // The run this editor reflects. Seeded from the reopened entity's OWN linked job (Workstream B) so a
  // reopened ad tracks its own generation, and a fresh "Create new" (no job_id) starts with none, so it does
  // not inherit an unrelated run (see the `job` memo below). Set again by startRun when a generation enqueues.
  const [jobId, setJobId] = React.useState<string | undefined>(reopen?.job_id || undefined);
  const [submitting, setSubmitting] = React.useState(false);
  // True while the LLM composes the storyboard for a just-picked promotion/template pair (seed-time
  // refine): the "seeding" phase, during which every beat renders as a shimmer skeleton. `refining` stays
  // owned by runRefine (set true at start, cleared in its `.finally`) so a failed/aborted/replayed compose
  // can never strand the loader.
  const [refining, setRefining] = React.useState(false);
  // The simulated type-in reveal that plays AFTER the refined beats arrive (decoupled from `refining`, which
  // only tracks the LLM call): the reveal effect below drives `step`/`typed` and clears this to null when it
  // finishes. Any path that swaps the draft outside a reveal (a new runRefine, the stored-replay branch, the
  // reopen branch) must reset it to null so a stale reveal can't type over the wrong beats.
  const [reveal, setReveal] = React.useState<ComposeReveal | null>(null);
  // True from a compose's start until the footer (model / aspect / Generate) has mounted AND finished its
  // entrance animation, so the storyboard button stays in its working state across the whole hand-off
  // (LLM call, then type-in reveal, then footer entrance) instead of going idle while the footer is missing.
  const [composeSettling, setComposeSettling] = React.useState(false);
  // The finished-ad player's url whose first frame has decoded (the player fades in then; see EasyVideo).
  const [readyVideo, setReadyVideo] = React.useState<string | null>(null);
  // The input signature the on-screen beats were last composed from (promotions + vibe + prompt). Set when a
  // compose lands or a saved ad is reopened; the easy editor compares it to the live inputs to mark the
  // storyboard stale (dimmed, needs regenerating) after the owner edits the setup. Null = never composed here.
  const [composedSig, setComposedSig] = React.useState<string | null>(null);
  // The art style the on-screen beats were composed in (the compose writes in its world; '' = no style), so picking
  // another one marks them stale too. Null = not known: a reopened storyboard does not record it, and a compose that
  // started before the brand's styles were read sent none (the service then used the active style).
  const [composedStyle, setComposedStyle] = React.useState<string | null>(null);
  // The language the on-screen storyboard was composed in ('' = none, an older ad): its call to action is written in
  // it, so a storyboard composed in another language than the ad's must be composed again before it renders. Null =
  // nothing composed here yet.
  const [composedLanguage, setComposedLanguage] = React.useState<string | null>(null);
  const composedLanguageRef = React.useRef(composedLanguage);
  composedLanguageRef.current = composedLanguage;
  // The promotions' copy in the ad's language as last read (set below, once the reads are wired), for a compose.
  const localizedCopiesRef = React.useRef<Readonly<Record<string, LocalizedCopy>>>({});
  // Single clip: why the last compose failed ('unsupported' = the backend answered without a shot plan, i.e. it
  // predates v2), shown in place of the plan; null when the last compose landed. v1 composes fail safe instead.
  const [composeError, setComposeError] = React.useState<'unsupported' | 'failed' | null>(null);
  // Single clip: the compose's own compiled prompt (keyed by its compile input), shown by the final-prompt panel
  // straight after a compose without a round trip.
  const [compiledSeed, setCompiledSeed] = React.useState<KeyedCompiledPrompt | null>(null);
  // The beat a live regenerate/resume run started from (drives the stale overlay while it is active),
  // and the beat the preview modal is showing (1-based; null = closed).
  const [resumeFromBeat, setResumeFromBeat] = React.useState<number | undefined>(undefined);
  // The job a regenerate/resume branched from, so the canvas can keep showing that run's frames/clips
  // (the new job's arrays fill in gradually) instead of blanking the whole board. Undefined on a fresh run.
  const [resumeOfId, setResumeOfId] = React.useState<string | undefined>(undefined);
  const [viewBeatIndex, setViewBeatIndex] = React.useState<number | null>(null);
  // Reopen (Workstream B): the saved key visual to fall back to when the source promotion is gone, and a
  // one-shot ref carrying the reopened draft so the load-or-refine effect replays it instead of re-composing.
  const [reopenKeyArt, setReopenKeyArt] = React.useState<string | undefined>(undefined);
  const reopenDraftRef = React.useRef<StoryboardDraft | null>(null);
  // The video-ad performance ("Stats") modal, opened from the easy-mode header (affiliate stats for this ad).
  // The stats sheet is the address's (`?stats=<id>`): Back from one of its deep links reopens it.
  const statsSheet = useSheetParam(STATS_SHEET_PARAM);
  // The promotion picker modal (multi-select over the brand's pool), shared with the carousel curation.
  // A brand-new promotion ad opens the promotions sheet straight away so the owner chooses what it advertises
  // (nothing is preselected).
  const [pickerOpen, setPickerOpen] = React.useState(
    () =>
      Boolean(reopen) &&
      subjectType === 'promotion' &&
      !(reopen?.storyboard?.promotions?.length ?? 0) &&
      !(reopen?.storyboard?.beats?.length ?? 0) &&
      items.length > 0,
  );
  const { closeHandle: pickerCloseHandle } = useSheetDismiss(() => setPickerOpen(false));

  // Which pipeline this draft renders with. Every beat-based path (stage parsing, per-beat errors, resume,
  // progress units, cost) branches on this first: a single-clip draft may still carry leftover v1 beats.
  const singleClip = draft.pipeline === 'single_clip';
  // The phone's rail tab, until the owner picks one: Video once a storyboard exists (its Generate is the next step),
  // Ingredients only while there is none to generate from. Here, above every early return, for useRailEnter.
  const railTab: ComposerRailTab =
    pickedRailTab ?? ((singleClip ? !draft.shotPlan : draft.beats.length === 0) ? 'ingredients' : 'output');
  const railEnter = useRailEnter(railTab, pickedRailTab !== null);

  // The anchor promotion (F0): the draft's first promotion, or, before any is picked (a fresh setup), the
  // brand's first pool item. DERIVED from the draft (react.md: derive, don't mirror) so the picker's
  // multi-select is the single source of truth and every promotion-keyed path reads promotions[0].
  const promotionId = draft.promotions[0]?.banner_id || (reopen ? '' : firstItem?.banner_id) || '';

  const item = React.useMemo(() => items.find((i) => i.banner_id === promotionId), [items, promotionId]);
  // What the ad advertises, as the tracking link's subject ("Quick landing page"): the pool item when the lead subject
  // is one (a promotion carries its on-site cta_href), else the lead subject as the draft holds it (a game, a feature).
  const leadPromotion = draft.promotions[0];
  const trackingSubject = React.useMemo<AdSubject | undefined>(() => {
    if (item) return adSubjectFromItem(item);
    if (!leadPromotion?.banner_id) return undefined;
    return {
      id: leadPromotion.banner_id,
      name: leadPromotion.headline ?? '',
      image_url: leadPromotion.image_url,
      headline: leadPromotion.headline ?? '',
      subtitle: leadPromotion.subtitle ?? '',
      cta_text: leadPromotion.cta_text ?? '',
      cta_href: '',
    };
  }, [item, leadPromotion]);
  // F0 = the first promotion's key visual once the draft carries promotions (seeded on Generate / hydrated on
  // reopen); before that (setup phase) it falls back to the currently-selected promotion's art, then the
  // reopened key visual. keyArtOf drives the multi-promo canvas + generation.
  const keyArt = keyArtOf(draft) ?? item?.image_url ?? reopenKeyArt;
  // The persisted entity being edited (Video Ads library). Every edit debounce-upserts to it (effect below).
  const entityId = reopen?.id;
  const brandInfo = React.useMemo(
    () => ({ name: brandName, domain: brandDomain, logo_url: brandLogoUrl }),
    [brandName, brandDomain, brandLogoUrl],
  );
  const { setTitleEditor } = useImmersive();
  // The easy modal's own animated dismissal (Sheet). No-op fallback until the library wires onClose.
  const { closeHandle: easyCloseHandle } = useSheetDismiss(onClose ?? (() => undefined));

  // Reconcile the draft's promotion(s) with the fetched pool. `items` loads async and CHANGES when the
  // owner switches brand, but this component instance persists, so the draft's promotions can be empty
  // (first load) or belong to the PREVIOUS brand. When NONE of them is in the current pool, reset to this
  // brand's first item; the load-or-refine / entity-upsert effects below persist + (re)seed for it. Guarded
  // by reopenKeyArt so a reopened ad whose promotion is gone keeps its saved key visual (never pruned).
  React.useEffect(() => {
    if (reopenKeyArt || items.length === 0) return;
    const cur = draftRef.current;
    if (cur.promotions.some((p) => items.some((i) => i.banner_id === p.banner_id))) return;
    // A video ad from the library never gets a promotion picked for it: an empty (brand-new) or stale (other
    // brand) selection is cleared and the promotions sheet opens so the owner chooses. Only the legacy
    // entity-less composer still defaults to the pool's first item.
    if (reopen) {
      if (cur.promotions.length > 0) setDraft(setPromotions(cur, []));
      // Only a brand-new ad asks: at mount the reopened draft is not applied yet (see reopenDraftRef), so an ad
      // that already has a storyboard is told apart by its saved beats / shot plan, not by the empty draft.
      const composed =
        cur.beats.length > 0 || (reopen.storyboard?.beats?.length ?? 0) > 0 || !!reopen.storyboard?.shot_plan;
      if (subjectType === 'promotion' && !composed) setPickerOpen(true);
      return;
    }
    setDraft(setPromotions(cur, [promotionFromItem(items[0])]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, items[0]?.banner_id]);

  // Stable indirection to the fork-on-edit guard (defined below, after the refs it needs). Edit handlers call
  // forkIfLockedRef.current() so they need not depend on forkIfLocked's identity or its declaration order.
  const forkIfLockedRef = React.useRef<() => void>(() => undefined);

  // Picking a template only changes the selection; the load-or-refine effect below handles seeding/replaying
  // (from the reload cache or a fresh LLM compose). Promotions are edited via the picker modal (below).
  const onPickTemplate = React.useCallback((value: string): void => {
    forkIfLockedRef.current(); // editing a locked (rendered) version forks a new latest first
    setTemplateId(value as StoryboardTemplateId);
  }, []);

  // Target length drives the beat count: it is sent on Generate storyboard, where the refine LLM makes the
  // per-beat durations (4/6/8) sum near it. Setting it changes the storyboard signature, so it reads as stale
  // and the Regenerate storyboard button lights up until re-composed. Held on the draft so it round-trips
  // through the version snapshot (persist + version-switch restore).
  const onPickDuration = React.useCallback((value: string): void => {
    forkIfLockedRef.current();
    setDraft((d) => ({ ...d, targetDurationSeconds: Number(value) || undefined }));
  }, []);

  // The owner picks the ad's language: an edit like any other ingredient (a locked version forks first). The
  // promotions' copy then syncs into it, and a composed storyboard reads stale until it is composed in it. A run
  // built in the old language is let go, so a retry can never resume beats drawn in another language.
  const onPickLanguage = React.useCallback((value: string): void => {
    if (value === (draftRef.current.language ?? '')) return;
    forkIfLockedRef.current();
    // A composed storyboard keeps the language it was composed in on the draft, so a reopened ad still asks for a new
    // compose (it would otherwise read as composed in the language just picked).
    setDraft((d) => ({
      ...d,
      language: value,
      composedLanguage:
        d.beats.length > 0 || d.shotPlan
          ? d.composedLanguage ?? composedLanguageRef.current ?? d.language ?? ''
          : undefined,
    }));
    setJobId(undefined);
    setResumeOfId(undefined);
    setResumeFromBeat(undefined);
  }, []);

  // Editing the steering prompt on a locked version forks a new latest (only the first keystroke of the burst
  // forks; forkIfLocked no-ops once the selection is unlocked).
  const onEditPrompt = React.useCallback((value: string): void => {
    forkIfLockedRef.current();
    setUserPrompt(value);
  }, []);

  // Seed-time LLM refine + reload persistence. Refs so the callbacks needn't re-create (and re-fire the
  // effect) when the promotions array, the draft, or the chosen model/aspect changes.
  const itemsRef = React.useRef(items);
  itemsRef.current = items;
  const aspectRef = React.useRef(draft.aspect);
  aspectRef.current = draft.aspect;
  const draftRef = React.useRef(draft);
  draftRef.current = draft;
  const videoModelRef = React.useRef(videoModel);
  videoModelRef.current = videoModel;
  const templateIdRef = React.useRef(templateId);
  templateIdRef.current = templateId;
  const userPromptRef = React.useRef(userPrompt);
  userPromptRef.current = userPrompt;
  // The current ad name, read inside the compose callback to decide whether to apply the LLM's suggested title
  // (only when the name is still a default, never over an owner-typed one).
  const nameRef = React.useRef(name);
  nameRef.current = name;
  // Resolve a promotion's START-FRAME art for the target video aspect: its own format whose aspect is CLOSEST
  // to the video's (a 9:16 video uses the 1:2 art, a 16:9 the 2:1, etc.), which the backend then cover-crops to
  // the exact target — so the branded key art survives instead of being reframed from a mismatched 2:1. Falls
  // back to the promotion's primary image when it has no per-aspect formats (game/feature subjects, old banners).
  const baseImageForAspect = React.useCallback((p: StoryboardPromotion, aspect: VideoAspectRatio): string => {
    const item = itemsRef.current.find((i) => i.banner_id === p.banner_id);
    const fmt = item ? bestFormatUrlForAspect(item, aspectRatioValue(aspect)) : undefined;
    return fmt || p.image_url;
  }, []);

  // Mood board: images the owner attaches to steer the whole video's look/feel. The persisted source of truth
  // is draft.moodImages (CDN urls). On attach we read each file to a data URI, upload it to CDN, then append the
  // returned url to the draft; while an upload is in flight it shows as a `pendingMood` thumbnail with a spinner.
  const [pendingMood, setPendingMood] = React.useState<{ key: string; dataUri: string }[]>([]);
  const pendingMoodRef = React.useRef(pendingMood);
  pendingMoodRef.current = pendingMood;

  const addMoodImages = React.useCallback(
    (files: File[]): void => {
      const room = MAX_MOOD_IMAGES - (draftRef.current.moodImages.length + pendingMoodRef.current.length);
      if (room <= 0) return;
      forkIfLockedRef.current();
      const images = files.filter((f) => f.type.startsWith('image/')).slice(0, room);
      images.forEach((file) => {
        const key = `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        readAsDataUri(file)
          .then((dataUri) => {
            setPendingMood((cur) => [...cur, { key, dataUri }]);
            return uploadArtwork(dataUri, brandName)
              .then((cdnUrl) => {
                setPendingMood((cur) => cur.filter((p) => p.key !== key));
                setDraft((d) => ({ ...d, moodImages: [...d.moodImages, cdnUrl].slice(0, MAX_MOOD_IMAGES) }));
              })
              .catch(() => {
                setPendingMood((cur) => cur.filter((p) => p.key !== key));
                onNotify(t('That image could not be added. Please try another.'));
              });
          })
          .catch(() => onNotify(t('That image could not be read. Please try another.')));
      });
    },
    [brandName, onNotify, t],
  );

  const removeMoodImage = React.useCallback((index: number): void => {
    forkIfLockedRef.current();
    setDraft((d) => ({ ...d, moodImages: d.moodImages.filter((_u, i) => i !== index) }));
  }, []);

  // The video-ad entity id scopes the storyboard cache (see storyboard-storage): a fresh "Create new" gets a
  // new id and so never replays a previous ad's compose (it composes fresh, showing the loader). Ref so the
  // refine/persist callbacks needn't re-create. Stable for the composer's lifetime (reopen is a mount prop).
  const entityIdRef = React.useRef(entityId);
  entityIdRef.current = entityId;
  const reopenRef = React.useRef(reopen);
  reopenRef.current = reopen;
  // Read onEntityPatch via a ref inside runRefine: adding it to that callback's deps would recreate runRefine
  // whenever the (often-unmemoized) prop changes, and the auto-refine effect depends on runRefine, so that would
  // re-trigger a compose on every render.
  const onEntityPatchRef = React.useRef(onEntityPatch);
  onEntityPatchRef.current = onEntityPatch;
  const refineCtrl = React.useRef<AbortController | null>(null);

  // Is the currently-selected version LOCKED (rendered)? Set from the finishedAd memo each render (below).
  // A ref so the edit handlers can read it without re-creating on every version switch.
  const selectedLockedRef = React.useRef(false);

  // Version LIFECYCLE fork: generating a video LOCKS its version; editing a locked version must not mutate
  // the rendered cut in place. So the first edit after a render FORKS a new unlocked "Latest" version
  // (snapshotting the current draft, which is the locked version's content) and switches to it. The imminent
  // edit then lands on this new version. A no-op when the selected version is already unlocked (editing the
  // latest in place) or when there is no entity to persist a fork onto.
  const forkIfLocked = React.useCallback((): void => {
    if (!selectedLockedRef.current || !entityIdRef.current || !tenantId) return;
    const versionSb = storyboardToSaved(
      draftRef.current,
      templateIdRef.current,
      videoModelRef.current,
      userPromptRef.current,
      { name: brandName, domain: brandDomain, logo_url: brandLogoUrl },
      subjectType,
      vibeOf(draftRef.current, templateIdRef.current),
    );
    const versionAt = new Date().toISOString();
    void upsertVideoDraft(tenantId, {
      id: entityIdRef.current,
      storyboard_version: versionSb,
      storyboard_version_at: versionAt,
    });
    onEntityPatchRef.current?.(entityIdRef.current, {
      video_versions: [{ storyboard: versionSb, created_at: versionAt }, ...(reopenRef.current?.video_versions ?? [])],
    });
    const k = versionKey(versionAt);
    setSelectedVersionKey(k);
    // Do NOT repoint renderTargetKey at the fork here: this session's completed render still exposes a `resultUrl`
    // (the job stays 'done'), and the finished-ad memo FILLS whatever renderTargetKey names with it. Pointing it
    // at the just-made storyboard-only fork would paint the old video onto it, so the fork would read as rendered
    // (locked) and the very next keystroke would fork AGAIN, spawning a new version per character. startRun sets
    // renderTargetKey to the selected version at actual Generate time, so the fork is still filled correctly then.
    // Immediately mark unlocked so the rest of this edit burst (and follow-ups) don't re-fork before the memo
    // recomputes on the new selection.
    selectedLockedRef.current = false;
  }, [tenantId, brandName, brandDomain, brandLogoUrl, subjectType, vibeOf]);
  forkIfLockedRef.current = forkIfLocked;

  // Save the current (draft, model) under the active (brand, promotion, template) so a reload replays it
  // instead of re-calling the LLM, and the owner's edits survive.
  const persistDraft = React.useCallback(
    (nextDraft: StoryboardDraft, nextModel: string): void => {
      if (!tenantId) return;
      // Fingerprint the whole promotion SET (not just F0) so switching between multi-promo sets that share a
      // first promotion can't collide in the reload cache.
      storeStoryboard(
        storyboardStorageKey(tenantId, promotionKey(nextDraft.promotions), templateIdRef.current, entityIdRef.current),
        nextDraft,
        nextModel,
      );
    },
    [tenantId],
  );

  // Owner edits on the canvas (beat role/motion/duration, aspect, cta) and add/remove/move beats: apply +
  // persist, so the edit survives a reload.
  const handleDraftChange = React.useCallback(
    (next: StoryboardDraft): void => {
      forkIfLockedRef.current(); // beat/aspect/cta edits on a locked (rendered) version fork a new latest first
      setDraft(next);
      persistDraft(next, videoModelRef.current);
    },
    [persistDraft],
  );

  const handleVideoModelChange = React.useCallback(
    (nextModel: string): void => {
      // Switching the model can snap the aspect/durations, i.e. it edits the setup — so on a locked (rendered)
      // version it must fork a new latest first, exactly like every other setup edit, never mutate the cut in place.
      forkIfLockedRef.current();
      setVideoModel(nextModel);
      // A different model may accept different durations AND aspect ratios — snap the beats so none can 500,
      // and snap the aspect to one the new model supports (Veo/Omni only do 16:9 / 9:16) so it can't 400.
      let snapped = snapDraftDurations(draftRef.current, durationsForModel(nextModel));
      const nextAspect = snapAspectToModel(snapped.aspect, nextModel);
      if (nextAspect !== snapped.aspect) snapped = { ...snapped, aspect: nextAspect };
      // Single clip: the clip length must be one the new model renders. Clamp it (which changes the setup, so the
      // plan reads stale) and tell the owner when the ad got shorter than the model can go.
      if (snapped.pipeline === 'single_clip') {
        const prevL = snapped.targetDurationSeconds;
        const nextL = clampClipLength(prevL, nextModel);
        if (nextL !== prevL) {
          snapped = { ...snapped, targetDurationSeconds: nextL };
          const label = VIDEO_MODELS.find((m) => m.id === nextModel)?.label ?? nextModel;
          const max = Math.max(...clipLengthsForModel(nextModel));
          if (prevL && prevL > max) {
            onNotify(
              t(
                '{{model}} makes clips up to {{max}}s, so the length is now {{n}}s. For a longer ad, pick Seedance 2.5 or use the Video Ads tab.',
                {
                  model: label,
                  max,
                  n: nextL,
                },
              ),
            );
          } else if (prevL) {
            onNotify(t('Clip length set to {{n}}s, a length {{model}} supports.', { n: nextL, model: label }));
          }
        }
      }
      if (snapped !== draftRef.current) setDraft(snapped);
      persistDraft(snapped, nextModel);
    },
    [persistDraft, onNotify, t],
  );

  // Single clip: an owner edit to the music line or a shot (action / camera / sound). Forks a locked version first;
  // it is the owner's own content, so it does not mark the plan stale (like a v1 beat edit).
  const onEditShotPlan = React.useCallback((plan: ShotPlan): void => {
    forkIfLockedRef.current();
    setDraft((d) => ({ ...d, shotPlan: plan }));
  }, []);

  // Applying a new promotion SET (from the picker modal or a chip's remove): write it into the draft
  // (setPromotions remaps each beat's promotion to survive the change), persist, and invalidate any run
  // built on the old set so Regenerate can't resume a mismatched generation.
  const applyPromotions = React.useCallback(
    (next: StoryboardPromotion[]): void => {
      // Never let the subject set go empty: an ad must keep at least one subject. Emptying it would strip the
      // ad of what it advertises and (with no persisted subject_type) let it re-infer to the wrong category on
      // reopen (e.g. a game ad turning into a promotion). Removing the last chip / an empty confirm is a no-op.
      if (next.length === 0) return;
      forkIfLockedRef.current(); // changing the subject set on a locked version forks a new latest first
      const nd = setPromotions(draftRef.current, next);
      setDraft(nd);
      persistDraft(nd, videoModelRef.current);
      setJobId(undefined);
      setResumeOfId(undefined);
      setResumeFromBeat(undefined);
    },
    [persistDraft],
  );

  // Toggle a pool banner in/out of the promotion set (the picker modal's card click). Self-gates the max
  // (a disabled card also blocks in the sheet, but the handler must not depend on styling — react.md).
  const onTogglePromotion = React.useCallback(
    (bannerId: string): void => {
      const cur = draftRef.current.promotions;
      const has = cur.some((p) => p.banner_id === bannerId);
      if (!has && cur.length >= maxSubjectsForLength(draftRef.current.targetDurationSeconds)) return;
      if (has) {
        applyPromotions(cur.filter((p) => p.banner_id !== bannerId));
        return;
      }
      const picked = itemsRef.current.find((i) => i.banner_id === bannerId);
      if (!picked) return;
      applyPromotions([...cur, promotionFromItem(picked)]);
    },
    [applyPromotions],
  );

  // Remove one promotion via its chip (by banner id; matches the legacy empty-id promo too).
  const onRemovePromotion = React.useCallback(
    (bannerId: string): void => {
      const cur = draftRef.current.promotions;
      // The last subject can't be removed (applyPromotions keeps at least one), so its X opens the subject's
      // picker (promotions / games / gamification) to swap it instead.
      if (cur.length === 1 && cur[0].banner_id === bannerId) {
        setPickerOpen(true);
        return;
      }
      applyPromotions(cur.filter((p) => p.banner_id !== bannerId));
    },
    [applyPromotions],
  );

  const openPicker = React.useCallback((): void => setPickerOpen(true), []);

  // Refine the (promotion, template) pair: seed the base beats locally, ask the backend LLM to rewrite
  // them into a higher-converting storyboard, and swap them in when they arrive. Cached per pairing so
  // re-selecting is instant; a new pick aborts the in-flight refine; any failure keeps the base beats.
  const runRefine = React.useCallback(
    (promo: string, tpl: StoryboardTemplateId): void => {
      const picked = itemsRef.current.find((i) => i.banner_id === promo);
      // Compose over the DRAFT's promotions when set (the picker's multi-select), else the just-picked one.
      // The LLM auto-distributes them across the beats. F0 (promotions[0]) is the base image the frames edit.
      const promotions =
        draftRef.current.promotions.length > 0
          ? draftRef.current.promotions
          : picked
            ? [promotionFromItem(picked)]
            : [];
      // Seed the compose's base image from the promotion's aspect-appropriate format (falls back to its primary).
      const artUrl = (promotions[0] ? baseImageForAspect(promotions[0], aspectRef.current) : '') || picked?.image_url;
      if (!tenantId || promotions.length === 0 || !artUrl) return;
      // Games / gamification carry no on-site CTA copy (a promotion brings its own cta_text), so fill a
      // subject default ("Play now" / "Join now") on each promotion. spark's buildRefinePrompt already
      // MANDATES the closing call-to-action beat and reads its text from the promotion/brand cta_text, so
      // supplying it is the whole fix. One map feeds seedDraft (base draft cta + template {{cta}} slot), the
      // compat cta arg and brand.cta_text alike; promotions are untouched (they already carry cta_text). An ad with a
      // language gets the default in that language. The language is captured here, so the stale signature records the
      // one the compose was told.
      const composeLanguage = (draftRef.current.language ?? '').trim();
      const subjectCtaText = subjectCta(subjectType, t, composeLanguage);
      // A compose in another language than the storyboard on screen starts from the CTAs in the new one: the earlier
      // compose wrote its CTA into the promotions, and a compose that falls back would keep it.
      const relanguaged =
        composeLanguage !== '' &&
        composedLanguageRef.current !== null &&
        composedLanguageRef.current !== composeLanguage;
      const promotionsIn = relanguaged
        ? promotionsCtaIn(promotions, localizedCopiesRef.current, composeLanguage, subjectType, subjectCtaText)
        : promotions;
      const composePromos =
        subjectCtaText === ''
          ? promotionsIn
          : promotionsIn.map((p) => ({ ...p, cta_text: p.cta_text?.trim() ? p.cta_text : subjectCtaText }));
      refineCtrl.current?.abort();
      const ctrl = new AbortController();
      refineCtrl.current = ctrl;
      setRefining(true);
      setComposeSettling(true);
      setReveal(null); // supersede any in-flight type-in from a previous compose
      // Capture the exact prompt + model this compose runs with, so the stale-signature matches what the backend saw.
      const composePrompt = userPromptRef.current.trim();
      const model = videoModelRef.current;
      const composeStyle = styleIdRef.current;
      // The style the beats are written in: '' for no style; unknown (null) while the brand's styles are being read.
      const composedIn = styleLoadedRef.current ? (composeStyle ?? '') : null;
      const endCompose = (): void => {
        if (refineCtrl.current === ctrl) {
          setRefining(false);
          refineCtrl.current = null;
        }
      };
      // Land a composed storyboard (either pipeline): apply it, name a still-unnamed ad, record the inputs it was
      // composed from (stale detection), append it as its own version, and cache it for a reload.
      const land = (composed: StoryboardDraft, title: string | undefined): void => {
        // Applied, versioned and cached alike: the storyboard as composed, in the language it was composed in.
        const next: StoryboardDraft = { ...composed, composedLanguage: composeLanguage };
        setDraft(next);
        // Name a still-unnamed ad from the compose LLM's suggested title, so a freshly generated storyboard is
        // not left as "Untitled". Only when the name is empty or a known placeholder — never over an owner edit.
        if (title) {
          const cur = nameRef.current.trim();
          const isDefault = cur === '' || DEFAULT_AD_NAME_KEYS.some((k) => cur === k || cur === t(k));
          if (isDefault) setName(title);
        }
        // This storyboard now corresponds to this promotion set + vibe + prompt + mood board (+ the model for a
        // single clip); changing any marks stale.
        setComposedSig(
          storyboardSignature(
            promotions,
            tpl,
            composePrompt,
            next.moodImages,
            next.targetDurationSeconds,
            next.pipeline,
            model,
            composeLanguage,
          ),
        );
        setComposedStyle(composedIn);
        setComposedLanguage(composeLanguage);
        // A fresh storyboard supersedes any prior render's fill target (compose is gated while a render is in
        // flight, so this only clears a COMPLETED render's target). Prevents the finished-ad memo from filling
        // an old version once a new storyboard is the focus; a later Generate sets the target for THIS one.
        setRenderTargetKey(undefined);
        // Each compose is its OWN version: append the just-composed storyboard (no video yet) to the ad's
        // history so it can be returned to. Best-effort; the debounced draft upsert persists the working draft
        // separately. Optimistically patch the cached list so the picker shows it before any refetch.
        if (entityIdRef.current && tenantId) {
          const versionSb = storyboardToSaved(
            next,
            tpl,
            model,
            composePrompt,
            {
              name: brandName,
              domain: brandDomain,
              logo_url: brandLogoUrl,
            },
            subjectType,
            vibeOf(next, tpl),
          );
          // Mint the version's created_at on the FE and send it as the key, so the optimistic entry, the
          // selection and the persisted row all share ONE value: selection then survives a refetch (the server
          // stores this exact instant), and a later Generate can target this version to fill it in place.
          const versionAt = new Date().toISOString();
          void upsertVideoDraft(tenantId, {
            id: entityIdRef.current,
            storyboard_version: versionSb,
            storyboard_version_at: versionAt,
          });
          onEntityPatchRef.current?.(entityIdRef.current, {
            video_versions: [
              { storyboard: versionSb, created_at: versionAt },
              ...(reopenRef.current?.video_versions ?? []),
            ],
          });
          // Switch the version picker to this brand-new storyboard-only version so it is obvious a fresh
          // storyboard was made (keys are normalized epoch-ms, so this holds across the next refetch too).
          setSelectedVersionKey(versionKey(versionAt));
        }
        // Persist so a reload replays this instead of re-calling the LLM (keyed by the whole promotion set).
        if (tenantId)
          storeStoryboard(
            storyboardStorageKey(tenantId, promotionKey(promotions), tpl, entityIdRef.current),
            next,
            model,
          );
      };

      // SINGLE CLIP (v2): compose a timestamped shot plan for ONE subject at the exact clip length L (spec 4.3). The
      // current plan stays on the draft (under the compose loader) until the new one lands; a failure keeps it and
      // says why instead of silently falling back to v1.
      if (draftRef.current.pipeline === 'single_clip') {
        const L = clampClipLength(draftRef.current.targetDurationSeconds, model);
        const subject = composePromos[0];
        setComposeError(null);
        void dispatch(
          refineStoryboard({
            tenantId,
            templateId: tpl,
            brandName,
            brandDomain,
            brandLogoUrl,
            promotions: [subject],
            headline: subject.headline,
            subtitle: subject.subtitle,
            cta: subject.cta_text,
            baseImageUrl: artUrl,
            aspectRatio: aspectRef.current,
            beats: [],
            prompt: composePrompt || templateById(tpl).title(t), // required non-empty: the owner's prompt, else the template title
            referenceImageUrls: draftRef.current.moodImages.length > 0 ? draftRef.current.moodImages : undefined,
            domain: brandDomain,
            videoModel: model,
            targetDurationSeconds: L,
            pipeline: 'single_clip',
            subjectType,
            ownerPrompt: composePrompt,
            language: composeLanguage,
            vibe: vibeOf({ ...draftRef.current, promotions: [subject] }, tpl),
            styleId: composeStyle, // the compose writes in the picked style's world
            signal: ctrl.signal,
          }),
        )
          .unwrap()
          .then((res) => {
            if (ctrl.signal.aborted || !res.shotPlan) return;
            const cur = draftRef.current;
            // Adopt the LLM's acquisition CTA (composited over the closing shot) on the draft and the subjects.
            const cta = res.cta;
            const next: StoryboardDraft = {
              ...cur,
              pipeline: 'single_clip',
              targetDurationSeconds: L,
              shotPlan: res.shotPlan,
              promotions: cta ? composePromos.map((p) => ({ ...p, cta_text: cta })) : composePromos,
              cta: cta || (relanguaged ? subject.cta_text : '') || cur.cta || subject.cta_text || brandName,
            };
            land(next, res.title);
            // Show the compose's own compiled prompt at once (the panel recompiles only once an input changes).
            setCompiledSeed(
              res.compiled ? { key: compileKeyOf(res.shotPlan, model, L, next.aspect), compiled: res.compiled } : null,
            );
          })
          .catch((err: { error?: string } | undefined) => {
            if (ctrl.signal.aborted) return;
            setComposeError(err?.error === SINGLE_CLIP_UNSUPPORTED ? 'unsupported' : 'failed');
          })
          .finally(endCompose);
        return;
      }

      // STITCHED BEATS (v1): seed the base draft (new promo copy, current aspect), snap durations to the model, and
      // show it under the compose loader (its beats render as shimmer skeletons while `refining` is true).
      const durations = durationsForModel(model);
      const base = snapDraftDurations(
        // Carry the mood board (a global look/feel input, not tied to the promotion/template being (re)composed, so a
        // Regenerate must never drop the attached images).
        {
          ...seedDraft(
            templateById(tpl),
            composePromos,
            brandName,
            aspectRef.current,
            translate,
            draftRef.current.targetDurationSeconds,
          ),
          moodImages: draftRef.current.moodImages,
          // The ad's language is setup too: a recompose keeps it (seedDraft starts without one).
          language: draftRef.current.language,
        },
        durations,
      );
      setDraft(base);
      void dispatch(
        refineStoryboard({
          tenantId,
          templateId: tpl,
          brandName,
          // Each subject's ASPECT-appropriate format, exactly as the render sends them (genPromotions): the compose
          // marks every chapter's peak with its subject's key-art url, and the worker only recognises the url the
          // render request carries (a primary image here made every chapter after the first lose its peak).
          promotions: composePromos.map((p) => ({ ...p, image_url: baseImageForAspect(p, aspectRef.current) })),
          headline: composePromos[0].headline, // compat: the single-promo handler fields = the first promotion's
          subtitle: composePromos[0].subtitle,
          cta: composePromos[0].cta_text, // single-promo: spark renders this as the CTA (brand.cta_text)
          baseImageUrl: artUrl,
          aspectRatio: aspectRef.current,
          beats: base.beats,
          prompt: composePrompt || templateById(tpl).title(t), // the owner's prompt steers the compose; falls back to the template title (backend requires a non-empty prompt)
          referenceImageUrls: draftRef.current.moodImages.length > 0 ? draftRef.current.moodImages : undefined,
          domain: brandDomain,
          videoModel: model, // so the compose LLM applies the chosen model's prompt-engineering guide
          targetDurationSeconds: draftRef.current.targetDurationSeconds, // sizes the beat count to the chosen length
          subjectType, // what the ad advertises (persisted; shapes the story)
          ownerPrompt: composePrompt, // the owner's own prompt (storyboard.prompt), empty when none
          language: composeLanguage, // the ad's language: the compose writes the call to action in it
          styleId: composeStyle, // the compose writes in the picked style's world
          signal: ctrl.signal,
        }),
      )
        .unwrap()
        .then((res) => {
          if (ctrl.signal.aborted) return;
          // Snap the refined durations too — the LLM may return an unsupported value. ensureDistribution
          // clamps/back-fills each beat's promotion_index (the LLM may return an un-distributed all-zero set).
          // The compose's shared music brief rides the draft (hidden) so generate / resume send it. Set from THIS
          // compose only: a refine without one clears it (undefined), so a stale brief never outlives a recompose.
          const refinedDraft = snapDraftDurations(
            { ...base, beats: ensureDistribution(res.beats, promotions.length), music: res.music },
            durations,
          );
          // Adopt the LLM's acquisition CTA (register/join/claim, never "Deposit") as the ad's CTA so the render
          // uses it: it must land BOTH on the draft's `cta` (single-promo -> brand.cta_text) AND on every
          // promotion's cta_text (multi-promo -> the worker renders the beat's promotion CTA). Signature keys on
          // banner_id, so overwriting cta_text does not mark the storyboard stale. Empty CTA => keep the current.
          const acquisitionCta = res.cta;
          const next = acquisitionCta
            ? {
                ...refinedDraft,
                cta: acquisitionCta,
                promotions: refinedDraft.promotions.map((p) => ({ ...p, cta_text: acquisitionCta })),
              }
            : refinedDraft;
          land(next, res.title);
          // Play the simulated type-in: reveal the refined beats one by one (skip straight to done under
          // reduced-motion). The reveal effect reads the beat count from the draft we just set.
          setReveal(prefersReducedMotion() ? null : { step: 0, typed: 0 });
        })
        .catch(() => {
          /* fail-safe: the base beats already on screen stay; no reveal (they show as-is once refining ends) */
          setReveal(null);
        })
        .finally(endCompose);
    },
    [tenantId, brandName, brandDomain, brandLogoUrl, translate, dispatch, t, subjectType, baseImageForAspect, vibeOf],
  );

  // Reopen a saved video ad ONCE on mount (Workstream B): rebuild the full editable draft from its persisted
  // params, restore the template + model, and recover the promotion by matching the saved key visual. Seed the
  // (promotion, template) slot AND reopenDraftRef so the load-or-refine effect below replays the reopened draft
  // rather than composing afresh. If the promotion is gone, keep the saved key visual as the base (reopenKeyArt)
  // and set the draft directly. Runs once — reopen is a mount-time prop from the library.
  React.useLayoutEffect(() => {
    // Only a saved ad WITH beats (or a single-clip shot plan) hydrates a draft; a fresh "Create new" entity (empty
    // storyboard) seeds fresh.
    if (!reopen || (!reopen.storyboard?.beats?.length && !reopen.storyboard?.shot_plan)) return;
    const rebuilt = draftFromStoryboard(reopen.storyboard, reopen.image_url);
    const model = rebuilt.videoModel || videoModelRef.current;
    const tpl = rebuilt.templateId ?? templateIdRef.current;
    let snapped = snapDraftDurations(rebuilt.draft, durationsForModel(model));
    const promo = itemsRef.current.find((i) => i.image_url === reopen.image_url);
    // Legacy single-promo ad: draftFromStoryboard synthesizes its F0 with an empty banner id. When the source
    // promotion still exists in the pool, tie the synthesized promo to it so the picker shows it selected and
    // the reconcile effect (which keys on banner id) won't prune it.
    if (promo && snapped.promotions.length > 0 && !snapped.promotions[0].banner_id) {
      snapped = {
        ...snapped,
        promotions: snapped.promotions.map((p, i) => (i === 0 ? { ...p, banner_id: promo.banner_id } : p)),
      };
    }
    reopenDraftRef.current = snapped;
    setVideoModel(model);
    setTemplateId(tpl);
    setUserPrompt(rebuilt.prompt);
    // The reopened beats correspond to these saved inputs; the easy editor marks stale once the owner edits them.
    setComposedSig(
      storyboardSignature(
        snapped.promotions,
        tpl,
        rebuilt.prompt ?? '',
        snapped.moodImages,
        snapped.targetDurationSeconds,
        snapped.pipeline,
        model,
        snapped.composedLanguage ?? snapped.language,
      ),
    );
    setComposedStyle(null);
    setComposedLanguage(snapped.composedLanguage ?? snapped.language ?? '');
    if (promo) {
      // The derived anchor (promotions[0].banner_id) resolves from the rebuilt draft; just cache the replay.
      if (tenantId)
        storeStoryboard(
          storyboardStorageKey(tenantId, promotionKey(snapped.promotions), tpl, entityIdRef.current),
          snapped,
          model,
        );
    } else {
      // Source promotion gone: keep the saved key visual as the base (the F0 chip renders from image_url).
      setReopenKeyArt(reopen.image_url);
      setDraft(snapped);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On any (promotion, template) change (initial mount and page reload included), REPLAY a saved storyboard
  // if one exists (no LLM call, the owner's edits preserved), else seed + refine and save it. ENTITY FLOW:
  // after the one-time reopen hydration, nothing here changes beats. The owner drives compose/regenerate via
  // the setup-card button (create -> setup -> generate -> typewriter), so switching promotion/template never
  // fires the LLM OR silently replays a cache behind their back. Only the legacy, entity-less path below runs.
  React.useEffect(() => {
    if (!tenantId) return undefined;
    // Reopen: apply the reopened draft once (set by the hydration effect above), skipping replay/refine.
    if (reopenDraftRef.current) {
      setDraft(reopenDraftRef.current);
      setReveal(null); // a reopened draft is already written, never type it in
      reopenDraftRef.current = null;
      return undefined;
    }
    // Entity flow: button-driven only (guard BEFORE the replay/compose branches, so a promotion A->B->A switch
    // never auto-replays A's cached beats). The setup-card button is the sole path that changes beats.
    if (entityId) return undefined;
    const picked = itemsRef.current.find((i) => i.banner_id === promotionId);
    if (!picked?.image_url) return undefined;
    const stored = loadStoredStoryboard(storyboardStorageKey(tenantId, promotionId, templateId, entityId));
    if (stored) {
      // Snap a restored draft to the (restored or current) model's durations, in case an old cache holds
      // an unsupported value.
      const model = stored.videoModel || videoModelRef.current;
      setDraft(snapDraftDurations(stored.draft, durationsForModel(model)));
      if (stored.videoModel) setVideoModel(stored.videoModel);
      setReveal(null); // a replayed draft is already written, never type it in (and cancel a stale reveal)
      return undefined;
    }
    runRefine(promotionId, templateId);
    return () => refineCtrl.current?.abort();
  }, [promotionId, templateId, tenantId, entityId, runRefine]);

  // Drive the simulated type-in after a compose returns: type the current beat's motion prose in, then pause
  // and advance to the next beat, until every beat is revealed (then clear `reveal` and hand the cards back
  // to editing). This is client-side because the compose is a SINGLE non-streaming call (the beats all
  // arrive at once, so this animates their appearance rather than reflecting real streaming). The beat count
  // and each motion string are read from the live draft (a ref), so the effect keys only on `reveal` and one
  // timer is live at a time (its cleanup cancels a superseded tick). The `r.step === reveal.step` guard makes
  // a late timer a no-op if a new compose reset the reveal.
  React.useEffect(() => {
    if (!reveal) return undefined;
    const beats = draftRef.current.beats;
    if (reveal.step >= beats.length) {
      setReveal(null);
      return undefined;
    }
    const motion = beats[reveal.step]?.motion_intent ?? '';
    if (reveal.typed < motion.length) {
      // Chars per tick, sized so a beat types in ~CHUNK_STEPS ticks whatever its length (a long motion string
      // doesn't crawl), capped at the string length.
      const chunk = Math.max(1, Math.ceil(motion.length / STORYBOARD_COMPOSE.CHUNK_STEPS));
      const handle = window.setTimeout(() => {
        setReveal((r) =>
          r && r.step === reveal.step ? { step: r.step, typed: Math.min(r.typed + chunk, motion.length) } : r,
        );
      }, STORYBOARD_COMPOSE.TYPE_MS);
      return () => window.clearTimeout(handle);
    }
    // Current beat fully typed: hold a beat, then advance to the next (typed reset to 0).
    const handle = window.setTimeout(() => {
      setReveal((r) => (r && r.step === reveal.step ? { step: r.step + 1, typed: 0 } : r));
    }, STORYBOARD_COMPOSE.BEAT_PAUSE_MS);
    return () => window.clearTimeout(handle);
  }, [reveal]);

  // Ends composeSettling once the compose and its type-in are done and the footer (which mounts at that moment)
  // has played its staggered entrance: the last of its three children starts 2 staggers in.
  React.useEffect(() => {
    if (!composeSettling || refining || reveal !== null) return undefined;
    const ms = prefersReducedMotion()
      ? 0
      : STORYBOARD_COMPOSE.FOOT_ENTER_MS + STORYBOARD_COMPOSE.FOOT_ENTER_STAGGER_MS * 2;
    const id = window.setTimeout(() => setComposeSettling(false), ms);
    return () => window.clearTimeout(id);
  }, [composeSettling, refining, reveal]);

  // Reset from template: drop the saved storyboard for this set + template and re-seed + re-compose from
  // scratch. This is also the "Generate storyboard" action in the setup phase.
  const onResetFromTemplate = React.useCallback((): void => {
    // The promotions' copy is not in the ad's language yet: the compose would quote another language.
    if (copyPendingRef.current) return;
    if (tenantId)
      clearStoredStoryboard(
        storyboardStorageKey(tenantId, promotionKey(draftRef.current.promotions), templateId, entityId),
      );
    runRefine(promotionId, templateId);
  }, [tenantId, promotionId, templateId, entityId, runRefine]);

  // Persist the entity on every edit (Video Ads library): a debounced FULL-entity upsert (name + draft +
  // poster) so name / promotion / template / prompt / beats "stay there". Full-entity so the backend keeps
  // status + job_id (COALESCE). Only when there is an entity id (the library flow).
  React.useEffect(() => {
    if (!entityId || !tenantId) return undefined;
    const fields = {
      name,
      image_url: keyArt ?? '',
      storyboard: storyboardToSaved(
        draft,
        templateId,
        videoModel,
        userPrompt,
        brandInfo,
        subjectType,
        vibeOf(draft, templateId),
      ),
    };
    // Keep the library's cached list in sync with this edit IMMEDIATELY, so a reopen (the editor remounts on an
    // easy<->advanced switch) or the listing card reads the fresh value at once; the server write is debounced.
    onEntityPatch?.(entityId, fields);
    const handle = window.setTimeout(() => {
      void upsertVideoDraft(tenantId, { id: entityId, ...fields });
    }, 800);
    return () => window.clearTimeout(handle);
  }, [
    entityId,
    tenantId,
    name,
    keyArt,
    draft,
    templateId,
    videoModel,
    userPrompt,
    brandInfo,
    subjectType,
    vibeOf,
    onEntityPatch,
  ]);

  // Flush the latest entity state on EXIT so a title (or any edit) made inside the 800ms debounce window is
  // not lost when the owner navigates out before the debounced upsert above fires — the debounce's cleanup
  // cancels that pending write on unmount, which was dropping just-typed titles. The ref holds the LATEST
  // upsert closure (reassigned every render); the empty-deps effect runs its cleanup only on unmount.
  const flushEntityRef = React.useRef<() => void>(() => undefined);
  flushEntityRef.current = (): void => {
    if (!entityId || !tenantId) return;
    // Persist the latest state on close, and patch the cached list so the listing / a reopen reflects it at once
    // (the optimistic patch also covers edits made inside the 800ms debounce window that never got persisted).
    const fields = {
      name,
      image_url: keyArt ?? '',
      storyboard: storyboardToSaved(
        draft,
        templateId,
        videoModel,
        userPrompt,
        brandInfo,
        subjectType,
        vibeOf(draft, templateId),
      ),
    };
    onEntityPatch?.(entityId, fields);
    void upsertVideoDraft(tenantId, { id: entityId, ...fields });
  };
  React.useEffect(() => () => flushEntityRef.current(), []);

  // Render the name input in the top bar's breadcrumb slot while the immersive editor is open (replacing the
  // breadcrumb), so the ad is named where its title belongs. Cleared on unmount / exit.
  React.useEffect(() => {
    if (!immersed || !entityId) return undefined;
    setTitleEditor({ value: name, onChange: setName });
    return () => {
      // PageTransition does NOT freeze the URL for the OUTGOING page instance, so on ENTRY the exiting
      // library instance re-renders at the new /videos/:id, mounts this composer, then unmounts ~after the
      // slide, its cleanup would clobber the title editor the entering instance just set (same class of bug
      // as the immersive clobber, see index.tsx). Guard on the LIVE url: only clear when we are actually
      // leaving the editor. During a rename the url is still an editor route, so the re-run below re-sets it.
      const v = videoViewFromPath(window.location.pathname);
      if (v?.kind !== 'new' && v?.kind !== 'edit') setTitleEditor(null);
    };
  }, [immersed, entityId, name, setTitleEditor]);

  // The run to reflect: this session's job once enqueued, else the tenant's newest storyboard job
  // (reload survival). `active` follows the async run; a done run shows the ad, a failed one its copy.
  const jobsById = useAppSelector(selectCreativeJobsMap);
  // The per-beat credit price for the SELECTED model (video_beat:<model>, else the flat video_beat
  // fallback) so the up-front cost matches what the backend actually charges per model.
  const creditPricing = useAppSelector((state) => state.aiCredits.pricing);
  const perBeatCost = videoBeatCostFor(creditPricing, videoModel) ?? beatCost;
  const job = React.useMemo(() => {
    if (jobId) return jobsById[jobId];
    // Entity flow (Video Ads library): the ad owns its run via its linked job_id (seeded into jobId above).
    // Do NOT adopt the tenant's newest storyboard job here — that bled a PREVIOUS ad's failure/status onto a
    // fresh "Create new". No linked job = no run to reflect. The newestJobOfKind recovery is only for the
    // legacy, entity-less flow (a plain reload with no library entity).
    if (entityId) return undefined;
    return newestJobOfKind(Object.values(jobsById), tenantId, 'storyboard');
  }, [jobId, jobsById, tenantId, entityId]);
  const status = job?.status;
  const active = status === 'queued' || status === 'running';
  // A run the OWNER stopped (the Stop button) also lands as 'failed' on the backend, marked `cancelled_by_owner`
  // (or the provider's "context canceled"). It is NOT a real failure, so it is excluded from every failure
  // branch below and shown as a neutral "Stopped" instead.
  const cancelled = status === 'failed' && isCancelled(job?.error);
  const failed = status === 'failed' && !cancelled;
  // A content-policy block is NOT transient: the video provider refused the wording, so a blind retry
  // fails identically. We tell the owner to edit the beat instead of just offering "try again".
  const contentPolicy = failed && isContentPolicyFailure(job?.error);
  // Any OTHER deterministic provider rejection carries a reason from content-generation (a bad/unsupported
  // parameter, a non-policy refusal). Surface the reason so the owner knows what to fix. Content-policy is
  // checked first (it gets its own actionable copy), so this only lights up for non-policy rejections.
  const rejectionReason = failed && !contentPolicy ? providerRejectionReason(job?.error) : undefined;
  // A per-call timeout (slow submit-and-poll video model, e.g. Seedance): distinct owner hint (took too
  // long) rather than the generic failure copy.
  const timedOut = failed && !contentPolicy && !rejectionReason && isTimeoutFailure(job?.error);
  const resultUrl = status === 'done' ? job?.result_url : undefined;

  // ── Continuous progress bar ────────────────────────────────────────────────────────────────────────
  // The backend advances `completed` in discrete units (a frame + a clip per beat), so a raw completed/total
  // bar jumps then sits still while the current unit renders. We interpolate WITHIN the current unit over its
  // estimated time (from the model's per-beat estimate) so the bar creeps continuously; a real completion snaps
  // it forward. Anchored per unit: a slower-than-estimated unit only stalls its own ~10% tail, never the whole
  // bar. `unitAnchorRef` records the wall-clock when `completed` last advanced (reset between runs).
  const unitAnchorRef = React.useRef<{ completed: number; at: number }>({ completed: -1, at: 0 });
  React.useEffect(() => {
    if (!active) {
      unitAnchorRef.current = { completed: -1, at: 0 };
      return;
    }
    const c = job?.completed ?? 0;
    if (c !== unitAnchorRef.current.completed) unitAnchorRef.current = { completed: c, at: Date.now() };
  }, [active, job?.completed]);
  // Re-render on a fixed cadence while a run is in flight, so the time-based interpolation advances between the
  // sparse backend stage events (which is what makes the bar move continuously rather than only on completions).
  const [, setRunTick] = React.useState(0);
  React.useEffect(() => {
    if (!active) return undefined;
    const h = window.setInterval(() => setRunTick((n) => n + 1), STORYBOARD_COMPOSE.PROGRESS_TICK_MS);
    return () => window.clearInterval(h);
  }, [active]);

  // Live-run inputs for the canvas choreography: the incrementally-arriving generated end frames and
  // the id of the node currently being worked, shown while the run is in flight.
  // Keep the previous run's frames/clips on screen through a regenerate/resume. The new job's arrays
  // fill in gradually (the backend seeds the reused 1..K-1 prefix), so overlay them on the job we
  // branched from: nothing blanks, and a downstream beat keeps its OLD preview (grayed via
  // `staleFromBeat`) until the re-run replaces it. On a fresh run `sourceJob` is undefined (no overlay).
  const sourceJob = resumeOfId ? jobsById[resumeOfId] : undefined;
  const generatedFrames = React.useMemo(
    () => mergeByIndex(job?.frames, sourceJob?.frames),
    [job?.frames, sourceJob?.frames],
  );
  const displaySegments = React.useMemo(
    () => mergeByIndex(job?.segments, sourceJob?.segments),
    [job?.segments, sourceJob?.segments],
  );
  // Each beat's length, sizing its share of the run preview's filmstrip (BeatFilmstrip).
  const beatDurations = React.useMemo(() => draft.beats.map((b) => b.duration_seconds), [draft.beats]);
  const stage = job?.stage;
  const running = active;

  // The finished ad plays INSIDE the output ("Finished ad") canvas node. The owner can select which rendered
  // version plays (persisted version history is folded in below once wired); the newest is the default.
  // A version = a storyboard + its optional video. The picker is keyed by the version's `created_at` (its stable
  // id), NOT its url, because a storyboard-only version (composed, not yet rendered) has no url. Selecting a
  // version loads its storyboard into the editor (see onSelectVersion) and plays its video (if any).
  const [selectedVersionKey, setSelectedVersionKey] = React.useState<string | undefined>(undefined);
  // The version key this session's render is FILLING (set by startRun). When the render lands, its url is written
  // onto THAT existing version rather than prepended, mirroring the backend's fill-in-place. Undefined => prepend.
  const [renderTargetKey, setRenderTargetKey] = React.useState<string | undefined>(undefined);
  const finishedAd = React.useMemo(() => {
    // The persisted history (worker-written render versions + compose versions). THIS session's just-finished
    // render is reconciled in before any refetch: if it targets an existing version (compose -> Generate, or a
    // re-render), its url FILLS that entry in place (one version through its life); otherwise it is prepended as a
    // fresh version. Pure; the server row wins on refetch (same key, same url) so this is idempotent.
    const base = reopen?.video_versions ?? [];
    let merged = base;
    if (resultUrl) {
      const targetPresent =
        renderTargetKey !== undefined && base.some((v) => versionKey(v.created_at) === renderTargetKey);
      merged = targetPresent
        ? base.map((v) => (versionKey(v.created_at) === renderTargetKey ? { ...v, url: resultUrl } : v))
        : appendFinishedAdVersion(base, resultUrl, job?.updated_at ?? new Date().toISOString());
    }
    const versions = merged.map((v) => ({
      key: versionKey(v.created_at),
      url: v.url,
      storyboard: v.storyboard,
      createdAt: Date.parse(v.created_at),
      // Locked = rendered (generate locks it). From the backend flag; plus, optimistically, THIS session's
      // just-finished render (its url) before the row is refetched with locked=true.
      locked: v.locked || (resultUrl !== undefined && !!v.url && v.url === resultUrl),
    }));
    const selected = versions.find((v) => v.key === selectedVersionKey) ?? versions[0];
    return {
      versions,
      selectedVersion: selected,
      selectedVersionKey: selected?.key,
      poster: generatedFrames?.filter(Boolean).slice(-1)[0] ?? keyArt,
      downloadName: assetFilename(
        brandName,
        item?.headline || templateById(templateId).title(t),
        selected?.url ?? '',
        'mp4',
      ),
    };
  }, [
    resultUrl,
    renderTargetKey,
    reopen?.video_versions,
    job?.updated_at,
    selectedVersionKey,
    generatedFrames,
    keyArt,
    brandName,
    item,
    templateId,
    t,
  ]);
  // The hand-over to the finished ad (VideoReveal): a run that finishes 'done' while this view watches it leaves its
  // preview (the dots and the strip) up, laid over the new video until the video reveals through it. Set on the
  // active -> idle transition (state adjusted during render, React's pattern for a value derived from a change) and
  // cleared when the reveal ends, or when no video reaches this view (an unlinked entity) within HANDOVER_WAIT_MS.
  const [wasActive, setWasActive] = React.useState(active);
  const [handOver, setHandOver] = React.useState(false);
  if (active !== wasActive) {
    setWasActive(active);
    setHandOver(!active && status === 'done');
  }
  const handOverVideo = finishedAd?.selectedVersion?.url;
  React.useEffect(() => {
    // Syncs with a timer: the hand-over gives up when the finished run's video never shows up here.
    if (!handOver || handOverVideo) return undefined;
    const id = window.setTimeout(() => setHandOver(false), STORYBOARD_REVEAL.HANDOVER_WAIT_MS);
    return () => window.clearTimeout(id);
  }, [handOver, handOverVideo]);
  const endHandOver = React.useCallback(() => setHandOver(false), []);
  const finishedAdRef = React.useRef(finishedAd);
  finishedAdRef.current = finishedAd;
  // Mirror the selected version's locked state into the ref the edit handlers read (forkIfLocked). A RENDERED
  // version (has a url) is locked even without the backend flag (legacy renders), so editing it always forks.
  selectedLockedRef.current = Boolean(finishedAd.selectedVersion?.locked || finishedAd.selectedVersion?.url);
  // A click on a locked (disabled) field nudges the "New version from this" link (a shake), hinting that an edit needs
  // a fresh version. The nudge resets whenever the selected version changes (fork / switch / re-lock after a render),
  // so the shake replays ONLY on an actual locked-field click. Locked = a RENDERED version selected, nothing running
  // (the easy rail's lockedView).
  const lockedNudge = useLockedNudge(!active && !!finishedAd.selectedVersion?.url, finishedAd.selectedVersionKey);

  // ── The ad's language ──────────────────────────────────────────────────────────────────────────────────────
  // The website's languages (the Language picker's options, the default first) and the promotions' copy in the ad's
  // language. Both are written into the draft only as AUTOMATIC fills (setDraft, never the owner-edit path, which
  // forks), and never into a rendered (locked) version: its copy is what it was rendered with.
  const selectedLocked = Boolean(finishedAd.selectedVersion?.locked || finishedAd.selectedVersion?.url);
  const site = useSiteLanguages(tenantId);
  const localized = useLocalizedPromotions({
    tenantId,
    language: draft.language,
    subjectType,
    promotions: draft.promotions,
    items,
    enabled: !selectedLocked,
  });
  localizedCopiesRef.current = localized.copies;
  const hasStoryboard = singleClip ? Boolean(draft.shotPlan) : draft.beats.length > 0;
  // Syncs with the website-languages read: a draft with no language yet, nothing composed and not a rendered version
  // starts in the website's default (an ad minted before the read answered, or made before the field). A composed or
  // rendered one keeps '' and renders as before; the owner's first pick is a normal edit.
  React.useEffect(() => {
    if (site.status !== 'ready' || !site.known || !site.defaultLanguage) return;
    if (draft.language || hasStoryboard || selectedLocked) return;
    // Re-checked on the latest draft: a reopened storyboard may have been applied after this render.
    setDraft((d) =>
      d.language || d.beats.length > 0 || d.shotPlan ? d : { ...d, language: site.defaultLanguage },
    );
  }, [site.status, site.known, site.defaultLanguage, draft.language, hasStoryboard, selectedLocked]);
  // Syncs with the copy reads: once a promotion's copy in the ad's language has answered, write it into the draft
  // (localizePromotionsCopy is a no-op once in sync), outside a run or a compose (a compose sends the synced copy).
  React.useEffect(() => {
    if (localized.status === 'idle' || selectedLocked || active || refining) return;
    setDraft((d) => localizePromotionsCopy(d, localized.copies));
    // draft.promotions too: a version switch or a reopen swaps in promotions the copies have not been applied to.
    // localizePromotionsCopy returns the same draft once in sync, so this settles.
  }, [localized.status, localized.copies, selectedLocked, active, refining, draft.promotions]);
  // The promotions' copy is not in the ad's language yet (being read, failed, or not written in yet): composing or
  // rendering now would put another language on screen, so both wait (a failure waits for Try again).
  const copyPending =
    // A draft that will take the website's default language waits for the read (it gives up on its own after a few
    // seconds, and a failed read composes as before): a compose now would write the storyboard in no language.
    (!(draft.language ?? '').trim() && !hasStoryboard && !selectedLocked && site.status === 'loading') ||
    localized.status === 'loading' ||
    localized.status === 'error' ||
    (localized.status === 'ready' && localizePromotionsCopy(draft, localized.copies) !== draft);
  const copyPendingRef = React.useRef(copyPending);
  copyPendingRef.current = copyPending;
  // The storyboard on screen was composed in another language than the ad's: its call to action is in that one, so
  // it is composed again before it renders.
  const languageStale =
    hasStoryboard && composedLanguage !== null && composedLanguage !== (draft.language ?? '').trim();

  // Select a version by its key: switch the player to its video (if any) AND load its storyboard into the editor
  // (its beats + setup), so an older version can be viewed and edited. Editing + regenerating then makes a new
  // version, the selected one is preserved. A legacy render-only version has no storyboard: just switch the player.
  const onSelectVersion = React.useCallback(
    (key: string): void => {
      const v = finishedAdRef.current.versions.find((x) => x.key === key);
      if (!v) return;
      setSelectedVersionKey(key); // the player switches regardless
      // Loading the version's storyboard mutates the WHOLE draft; skip it while a run or compose is in flight
      // (like every other draft-swapping control) so it can't desync an active run. Cancel any in-flight type-in
      // reveal first, exactly as the compose/reopen/replay paths do.
      if (!v.storyboard || active || refining) return;
      setReveal(null);
      const rebuilt = draftFromStoryboard(v.storyboard, keyArt);
      const model = rebuilt.videoModel ?? videoModelRef.current;
      const snapped = snapDraftDurations(rebuilt.draft, durationsForModel(model));
      setDraft(snapped);
      setTemplateId((rebuilt.templateId ?? templateIdRef.current) as StoryboardTemplateId);
      setVideoModel(model);
      setUserPrompt(rebuilt.prompt);
      // The loaded storyboard is the one this version was rendered from, so it is NOT stale against its video.
      setComposedSig(
        storyboardSignature(
          snapped.promotions,
          rebuilt.templateId ?? templateIdRef.current,
          rebuilt.prompt,
          snapped.moodImages,
          snapped.targetDurationSeconds,
          snapped.pipeline,
          model,
          snapped.composedLanguage ?? snapped.language,
        ),
      );
      setComposedStyle(null);
      setComposedLanguage(snapped.composedLanguage ?? snapped.language ?? '');
      setComposeError(null);
    },
    [keyArt, active, refining],
  );
  // While a regenerate/resume run is in flight, beats at/after its `fromBeat` read as stale (queued to
  // be re-run). On reload survival (job recovered via newestJobOfKind) this is legitimately undefined,
  // as the job carries no `from_beat`.
  const staleFromBeat = active ? resumeFromBeat : undefined;

  // The storyboard button's working state: the compose, its type-in and the footer's entrance (composeSettling).
  const composing = refining || reveal !== null || composeSettling;

  // The compose loader passed to the canvas: the type-in reveal takes precedence; otherwise, while the LLM
  // is still running (`refining`), `step:-1` renders every beat as a shimmer skeleton (the seeding phase).
  // Null when neither is happening (a normal, resting canvas).
  const compose = React.useMemo(() => reveal ?? (refining ? { step: -1, typed: 0 } : null), [reveal, refining]);

  // On a failure, the beat to resume from: the first missing clip (segments[i] falsy for i in 1..N),
  // else parsed from the stalled stage id, else beat 1. Clamped to the current beat range.
  // The beat a failure belongs to, AND whether it genuinely failed ON a beat. `onBeat` is true only when
  // a beat's clip is missing OR the stalled stage names a frame/beat — NOT for an assembly/upload or a
  // pre-loop failure (missing ffmpeg, key-visual fetch), where `failedBeat` is just a clamped default of 1
  // and pinning an error to beat 1 would mislead (and resuming from 1 would needlessly re-run everything).
  const { failedBeat, failedOnBeat } = React.useMemo<{ failedBeat: number; failedOnBeat: boolean }>(() => {
    const n = draft.beats.length;
    // A single-clip run has no beats (its `segments` is always empty and its stages are not frame/beat ids), so a
    // failure is never pinned to a beat, even when the draft still carries leftover v1 beats.
    if (singleClip || n === 0) return { failedBeat: 1, failedOnBeat: false };
    const segs = job?.segments;
    let k = 0;
    if (segs) {
      for (let i = 1; i <= n; i += 1) {
        if (!segs[i]) {
          k = i;
          break;
        }
      }
    }
    let onBeat = k > 0;
    if (k === 0) {
      const m = /^(?:frame|beat)-(\d+)$/.exec(job?.stage ?? '');
      if (m) {
        k = Number(m[1]);
        onBeat = true;
      } else {
        k = 1;
      }
    }
    return { failedBeat: Math.min(Math.max(k, 1), n), failedOnBeat: onBeat };
  }, [job?.segments, job?.stage, draft.beats.length, singleClip]);

  // A short, owner-facing reason pinned under the beat a failed run stopped on (see StoryboardCanvas ->
  // graph -> BeatNode). Pre-translated here and rendered raw, so the reason (which may be provider text)
  // isn't re-run through i18n. Undefined unless the run failed.
  const beatError = React.useMemo<string | undefined>(() => {
    if (!failed) return undefined;
    if (contentPolicy) return t('Blocked by the content policy. Reword this beat, then resume.');
    if (rejectionReason) return t('Rejected: {{reason}}', { reason: rejectionReason });
    if (timedOut) return t('This beat took too long and was stopped. Try a shorter duration, then resume.');
    return t('Generation failed here. Resume to retry from this beat.');
  }, [failed, contentPolicy, rejectionReason, timedOut, t]);

  // A mature picked art style blocks every video render (the models refuse it); see `matureStyle`. So does one not
  // yet read (`styleChecking`), which may be mature. The mature note itself is keyed on `matureStyle` alone.
  const renderBlocked = matureStyle || styleChecking;

  const canGenerate =
    !renderBlocked &&
    Boolean(tenantId && keyArt) &&
    draftIsReady(draft) &&
    !submitting &&
    !active &&
    !refining &&
    !reveal && // still composing: block generate until the type-in reveal has finished
    !copyPending && // the promotions' copy is not in the ad's language yet
    !languageStale; // the storyboard was composed in another language

  // The single enqueue path for a fresh generation AND a regenerate/resume: same call, plus the optional
  // `resumeOf`/`fromBeat`. Always uses the CURRENT draft, so an edited beat K takes effect on resume.
  // The disabled state styles the button; the handler self-gates so a click can never slip through.
  const startRun = React.useCallback(
    async (extra?: { resumeOf?: string; fromBeat?: number }): Promise<void> => {
      if (renderBlocked || !tenantId || !keyArt || !draftIsReady(draft) || submitting || active) return;
      // Never render another language than the ad's (the copy is still being read, or the storyboard's call to
      // action was written in another one); a resume included, since its reused beats were drawn in the run's.
      if (copyPending || languageStale) return;
      const tpl = templateById(templateId);
      // Parent version = F0's promotion (promotions[0]); falls back to the selected item / undefined.
      const f0Item = itemsRef.current.find((i) => i.banner_id === draft.promotions[0]?.banner_id) ?? item;
      // FILL an existing version in place ONLY for the compose -> Generate flow: the selected version is an
      // UN-RENDERED storyboard (no video yet) whose content still matches the draft. Otherwise — the selected
      // version ALREADY HAS a video, or the storyboard was edited, or there is no version yet — this render gets
      // its OWN brand-new version, created below (on success) as a generating placeholder the UI moves to at once.
      // (A retry still fills: the failed target is a storyboard-only row with no url, so canFill stays true.)
      const sel = finishedAdRef.current.selectedVersion;
      const canFill = !!(
        sel &&
        !sel.url &&
        sel.storyboard &&
        Number.isFinite(sel.createdAt) &&
        draftMatchesVersion(draft, tpl.id, userPrompt, sel.storyboard, keyArt)
      );
      // The version this render writes to, keyed by a created_at the FE mints so the optimistic entry, the
      // selection and the backend fill all agree. Fill reuses the selected version's key; a new version gets a
      // fresh one. `targetAt` is RFC 3339 (backend timestamptz match); `targetKey` is the normalized FE key.
      const targetAt = canFill && sel ? new Date(sel.createdAt).toISOString() : new Date().toISOString();
      const targetKey = canFill && sel ? sel.key : versionKey(targetAt);
      // Seed the render's start frames from each promotion's ASPECT-APPROPRIATE format (a 9:16 uses the 1:2 art,
      // a 16:9 the 2:1, …), which the backend cover-crops to the exact target — so the branded key art survives
      // instead of being reframed from a mismatched 2:1. Only the RENDER request uses these; the draft, keyArt
      // and the stored version keep their primary images, so version fill-matching (draftMatchesVersion) is
      // unaffected. Falls back to each promotion's primary image when it has no per-aspect formats.
      // Single clip (spec 4.5): exactly ONE subject, `beats: []`, the clip length + shot plan, and never a resume.
      const singleClipRun = draft.pipeline === 'single_clip';
      const runExtra = singleClipRun ? undefined : extra;
      const genPromotions = (singleClipRun ? draft.promotions.slice(0, 1) : draft.promotions).map((p) => ({
        ...p,
        image_url: baseImageForAspect(p, draft.aspect),
      }));
      const genBaseImage = genPromotions[0]?.image_url || keyArt;
      const ownerPrompt = userPrompt.trim();
      const vibe = vibeOf(draft, tpl.id);
      setSubmitting(true);
      try {
        const res = await dispatch(
          enqueueGeneration({
            tenantId,
            placement: STORYBOARD_PLACEMENT,
            // A non-empty prompt is REQUIRED by the backend: v1 sends the template title; a single clip sends the
            // owner's prompt, else the template title (spec 4.2).
            prompt: singleClipRun ? ownerPrompt || tpl.title(t) : tpl.title(t),
            baseImageUrl: genBaseImage,
            parentVersionId: f0Item ? keyArtVersionId(f0Item) : undefined,
            mediaType: 'storyboard',
            aspectRatio: draft.aspect,
            storyboard: toStoryboardInput(
              { ...draft, promotions: genPromotions },
              tpl.id,
              {
                name: brandName,
                domain: brandDomain,
                logo_url: brandLogoUrl,
                cta_text: draft.cta || undefined,
              },
              videoModel,
              { subjectType, prompt: ownerPrompt, vibe },
            ),
            // Mood-board refs steer every beat's frame render (and the compose already baked them into the beats).
            referenceImageUrls: draft.moodImages.length > 0 ? draft.moodImages : undefined,
            // Undefined on a fresh generate (always, for a single clip); the body builder omits both keys then.
            resumeOf: runExtra?.resumeOf,
            fromBeat: runExtra?.fromBeat,
            // The version this render fills, by created_at (RFC 3339): the existing selected one (fill) or the new
            // placeholder created on success below. The backend prepends only as a fallback if that row is missing.
            targetVersionAt: targetAt,
            mascotId: mascotPick || undefined,
            // The picked art style. A resume carries its source run's style (the reused beats were drawn in it), so it
            // sends none.
            styleId: runExtra?.resumeOf ? undefined : styleRun.sendStyleId,
          }),
        ).unwrap();
        if (!res.ok) {
          if (res.needed !== undefined) onBuyCredits(res.needed);
          else onNotify(t("That couldn't be started. Please try again."));
          return;
        }
        setJobId(res.jobId);
        // Render into a NEW version (not filling an existing storyboard): create that version NOW as a
        // storyboard-only placeholder, so the picker gains it immediately and the UI can move to it and show the
        // generating state before the mp4 lands (the render fills it on completion, keyed by targetAt). This is
        // what makes "Generate from a version that already has a video" spawn a fresh version at once.
        if (!canFill && entityId && tenantId) {
          const placeholderSb = storyboardToSaved(
            draft,
            tpl.id,
            videoModel,
            userPrompt,
            {
              name: brandName,
              domain: brandDomain,
              logo_url: brandLogoUrl,
            },
            subjectType,
            vibe,
          );
          void upsertVideoDraft(tenantId, {
            id: entityId,
            storyboard_version: placeholderSb,
            storyboard_version_at: targetAt,
          });
          onEntityPatchRef.current?.(entityId, {
            video_versions: [
              { storyboard: placeholderSb, created_at: targetAt },
              ...(reopenRef.current?.video_versions ?? []),
            ],
          });
        }
        // Move the version picker to the target version so the run's progress shows ON it (a storyboard-only
        // target renders the generating state; on completion its url fills in and it plays).
        setRenderTargetKey(targetKey);
        setSelectedVersionKey(targetKey);
        // Link the generating job to the entity so the library reconciles the finished mp4 back onto it, and
        // patch the cached list's job_id at once so a same-tab reopen keeps showing progress (else the run looks
        // lost). An optimistic patch, not a refetch, so it never races a stale server read over a fresh edit.
        if (entityId && tenantId) {
          onEntityPatch?.(entityId, { job_id: res.jobId });
          void upsertVideoDraft(tenantId, { id: entityId, job_id: res.jobId });
        }
        // Remember which beat this run started from (undefined for a fresh generate) so the canvas can
        // mark beats K..N stale while it is in flight.
        setResumeFromBeat(runExtra?.fromBeat);
        setResumeOfId(runExtra?.resumeOf);
        void dispatch(fetchOwnerWallets());
      } catch {
        onNotify(t("That couldn't be started. Please try again."));
      } finally {
        setSubmitting(false);
      }
    },
    [
      renderBlocked,
      entityId,
      tenantId,
      item,
      keyArt,
      draft,
      submitting,
      active,
      templateId,
      videoModel,
      userPrompt,
      brandName,
      brandDomain,
      brandLogoUrl,
      dispatch,
      onBuyCredits,
      onNotify,
      onEntityPatch,
      t,
      subjectType,
      baseImageForAspect,
      vibeOf,
      mascotPick,
      styleRun.sendStyleId,
      copyPending,
      languageStale,
    ],
  );

  // Regenerate the ad from beat K (1-based): a resume run branched off the CURRENT job. Guarded so it
  // needs a source job and never starts a second run while one is active (startRun re-checks too). v1 only: a
  // single clip has no partial resume (the backend 400s it).
  const regenerateFrom = React.useCallback(
    (k: number): void => {
      setViewBeatIndex(null); // if triggered from the preview modal, close it
      if (renderBlocked || !job || active || singleClip) return;
      void startRun({ resumeOf: job.id, fromBeat: k });
    },
    [renderBlocked, job, active, singleClip, startRun],
  );

  // Redraw only the animated copy of the ad's latest render (LOCAL_COPY_REDO): a rerun from beat N+1 reuses every beat
  // (and the render's stored base), so it costs image draws, not video. It always branches off the latest job, so it
  // is offered only while that job's render is the selected version.
  const redrawCopy = React.useCallback((): void => {
    if (!LOCAL_COPY_REDO) return;
    regenerateFrom(draft.beats.length + 1);
  }, [regenerateFrom, draft.beats.length]);

  // Retry a FAILED run straight from the inline error (easy mode). Resume from the beat that failed, reusing the
  // completed beats before it so only the remaining beats are re-charged; a fresh run when the failure was not on
  // a specific beat or was beat 1 (a resume needs from_beat >= 2), and ALWAYS for a single clip (one clip, no
  // partial resume). Both actions self-guard on an in-flight run.
  const retryRun = React.useCallback((): void => {
    if (renderBlocked) return;
    if (!singleClip && failedOnBeat && failedBeat >= 2 && job) regenerateFrom(failedBeat);
    else void startRun();
  }, [renderBlocked, singleClip, failedOnBeat, failedBeat, job, regenerateFrom, startRun]);

  // Stop an in-flight run (the Stop button): cancel + refund it on the backend, and drop the local run overlay
  // so the editor returns to an editable state. Self-gates so a click can never fire when nothing is running.
  const stopRun = React.useCallback((): void => {
    if (!active || !job || !tenantId) return;
    void dispatch(cancelJob({ tenantId, jobId: job.id }));
    setResumeFromBeat(undefined);
    setResumeOfId(undefined);
  }, [active, job, tenantId, dispatch]);

  const viewBeat = React.useCallback((index: number): void => setViewBeatIndex(index), []);

  // Card label for a pool banner in the promotion picker (headline > internal name > a generic fallback).
  const promotionLabel = React.useCallback(
    (i: CreativeItem): string => i.headline || i.internal_name || subjectChipLabel,
    [subjectChipLabel],
  );
  // Banner ids currently in the promotion set (drives the picker's checks + max gate). For a game /
  // gamification ad these are the chosen game / feature ids, so they pre-select the dedicated browser on Edit.
  const selectedPromotionIds = React.useMemo(
    () => draft.promotions.map((p) => p.banner_id).filter((id) => id !== ''),
    [draft.promotions],
  );

  // The picker trigger's label, subject-aware and reflecting whether anything is chosen yet, so a games /
  // gamification ad reads "Edit games" / "Edit gamification features" instead of "Edit promotions".
  const subjectHasSelection = draft.promotions.length > 0;
  const subjectButtonLabel =
    subjectType === 'game'
      ? subjectHasSelection
        ? t('Edit games')
        : t('Choose games')
      : subjectType === 'gamification'
        ? subjectHasSelection
          ? t('Edit gamification features')
          : t('Choose gamification features')
        : subjectHasSelection
          ? t('Edit promotions')
          : t('Choose promotions');

  // Editing a games / gamification ad opens the FULL dedicated browser (tabs + search for games, the active
  // set for gamification) rather than the promotion PoolPickerSheet scoped to the current picks; confirm maps
  // the chosen subjects to promotions and applies them (setPromotions clamps to the cap and remaps beats).
  const handleGamesEdited = React.useCallback(
    (games: GameSourceRecord[]): void => {
      applyPromotions(games.map((g) => storyboardPromotionFromGame(g, draftRef.current.aspect)));
      setPickerOpen(false);
    },
    [applyPromotions],
  );
  const handleFeaturesEdited = React.useCallback(
    (features: GamificationFeature[]): void => {
      applyPromotions(features.map(storyboardPromotionFromFeature));
      setPickerOpen(false);
    },
    [applyPromotions],
  );
  const templateOptions = React.useMemo<SelectOption[]>(
    () => STORYBOARD_TEMPLATES.map((tpl) => ({ value: tpl.id, label: tpl.title(t) })),
    [t],
  );
  // The advanced panel's Language options: the website's languages, each named in its own tongue (raw labels).
  const languageOptions = React.useMemo<SelectOption[]>(
    () => site.languages.map((language) => ({ value: language, label: adLanguageLabel(language) })),
    [site.languages],
  );
  const languageUnknown = site.status === 'error' || (site.status === 'ready' && !site.known);
  // Model labels are product names (proper nouns), rendered as-is (no t()). A single clip quotes each model's
  // whole-clip time instead of its per-beat time.
  const videoModelOptions = React.useMemo(
    () =>
      selectableVideoModels().map((m) => ({
        value: m.id,
        label: m.label,
        description: <ModelOptionMeta model={m} perClip={singleClip} />,
      })),
    [singleClip],
  );
  // Single clip: the lengths the chosen model renders as ONE clip (Omni picks its own, so it reads "≈10s").
  const clipLengthOptions = React.useMemo<SelectOption[]>(
    () =>
      clipLengthsForModel(videoModel).map((L) => ({
        value: String(L),
        label: isApproxClipLength(videoModel) ? `≈${L}s` : `${L}s`,
      })),
    [videoModel],
  );
  // Durations the chosen model accepts — the beat duration dropdown options (Veo: 4/6/8).
  const videoDurations = React.useMemo(() => durationsForModel(videoModel), [videoModel]);
  const videoAspects = React.useMemo(() => aspectsForModel(videoModel), [videoModel]);

  // The subject picker opened by the "Edit …" trigger. A promotion ad keeps the banner PoolPickerSheet; a
  // games / gamification ad opens its FULL dedicated browser instead (so Edit shows the whole catalogue /
  // active set, pre-selected with the current picks), not a popup scoped to what is already chosen. Same
  // element in both the easy modal and the immersive canvas render paths below.
  const subjectPickerNode =
    subjectType === 'game' ? (
      <GamePickerSheet
        initialSelectedIds={selectedPromotionIds}
        max={maxSubjectsForLength(draft.targetDurationSeconds)}
        onConfirm={handleGamesEdited}
        onClose={() => setPickerOpen(false)}
      />
    ) : subjectType === 'gamification' ? (
      <GamificationPickerSheet
        initialSelectedIds={selectedPromotionIds}
        max={maxSubjectsForLength(draft.targetDurationSeconds)}
        onConfirm={handleFeaturesEdited}
        onClose={() => setPickerOpen(false)}
      />
    ) : (
      <PoolPickerSheet
        title={subjectPickerTitle}
        poolItems={items}
        selectedIds={selectedPromotionIds}
        labelOf={promotionLabel}
        onToggle={onTogglePromotion}
        onClose={() => setPickerOpen(false)}
        closeHandle={pickerCloseHandle}
        max={maxSubjectsForLength(draft.targetDurationSeconds)}
        emptyText="This brand has no promotions yet. Create one in All Promotions & Creatives first."
        hint="Pick one or more. The first is the ad's key visual; the AI arranges them into beats with smooth transitions. Reorder them on the timeline."
      />
    );

  if (items.length === 0) {
    // Wrap in Root so the empty copy stays centred in the collapsed immersive surface (a bare EmptyText
    // would render flush top-left once the layout gutters are gone).
    return (
      <Root $immersive={immersed}>
        <EmptyText text="Once this brand has promotions, you can turn one into a storyboard ad here." />
      </Root>
    );
  }

  const progressValue = job && job.total ? Math.round((100 * (job.completed ?? 0)) / job.total) : 0;
  // On a RESUME/retry run there is a gap between enqueue and the backend's first stage event where `job.stage`
  // is still empty; the beats would all read idle and the footer "Starting...", exactly as if it were a FRESH
  // generation. We already know K (`resumeFromBeat`, 1-based) the moment Retry is hit, so for that gap synthesize
  // a `frame-K` stage: the reused prefix (beats 1..K-1) shows done and beat K shows active immediately, matching
  // where the run actually resumes. Once the real stage arrives it takes over (job.stage wins).
  // v1 only: a single-clip stage is `prepare` / `generate` / `finishing` / `done` and never resumes.
  const effectiveStage = singleClip
    ? job?.stage
    : job?.stage || (active && resumeFromBeat !== undefined ? `frame-${resumeFromBeat}` : undefined);
  // Advance the bar to the reused prefix on a resume (each done beat = frame + clip = 2 of 2N+1 units) so it does
  // not start near-empty; the live progress takes over via max() once it passes the prefix.
  const resumePrefixPct =
    !singleClip && active && resumeFromBeat !== undefined && draft.beats.length > 0
      ? Math.round((100 * 2 * (resumeFromBeat - 1)) / (2 * draft.beats.length + 1))
      : 0;
  const effectiveProgress = Math.max(progressValue, resumePrefixPct);
  // Time-based smoothing (see unitAnchorRef above): fill the current unit's slice over its estimated duration,
  // capped just shy of the next milestone so it never implies an unfinished completion; floored by the real
  // progress so a faster-than-estimated run snaps forward; and held below 100 until the run actually ends. Only
  // consumed while `active` (the bars render only then), so a non-active fallback keeps the discrete value.
  // A single clip reports 4 units (opening frame, clip, overlay, upload) with per-unit weights from etaMinPerClip.
  const totalUnits =
    job?.total && job.total > 0 ? job.total : singleClip ? SINGLE_CLIP_UNITS : Math.max(1, 2 * draft.beats.length + 1);
  const completedUnits = job?.completed ?? 0;
  const estUnitMs = runUnitMs({
    pipeline: singleClip ? 'single_clip' : 'beats',
    beats: draft.beats.length,
    total: totalUnits,
    completed: completedUnits,
    model: videoModel,
  });
  const unitAnchor = unitAnchorRef.current;
  const inUnitMs = unitAnchor.at > 0 && unitAnchor.completed === completedUnits ? Date.now() - unitAnchor.at : 0;
  const interpolatedProgress = runProgressPct({ completed: completedUnits, total: totalUnits, inUnitMs, estUnitMs });
  const smoothProgress = active ? Math.min(97, Math.max(effectiveProgress, interpolatedProgress)) : effectiveProgress;

  // The control card content: promotion picker, a free-text prompt that steers the compose, and Regenerate.
  // Immersive renders it INSIDE the on-canvas `promo` node (tied to the key visual by a connector); mobile
  // stacks it at the top of the column. Same element either way.
  const leftPanelNode = (
    <LeftControls>
      <PromotionsField
        label={subjectFieldLabel}
        buttonLabel={subjectButtonLabel}
        promotions={draft.promotions}
        emptyChipLabel={subjectChipLabel}
        onOpen={openPicker}
        onRemove={onRemovePromotion}
        removeAriaLabel={(promoName) => t('Remove {{name}}', { name: promoName })}
        disabled={refining || reveal !== null || active}
      />
      {/* The ad's language: the website's languages, named in their own tongue (raw, never translated). */}
      <StudioSelect
        label={t('Language')}
        rawLabels
        value={draft.language ?? ''}
        onChange={onPickLanguage}
        options={languageOptions}
        placeholder={adLanguageLabel(site.defaultLanguage)}
        disabled={refining || reveal !== null || active || site.status !== 'ready' || !site.known}
      />
      {languageUnknown ? <HintText text="The website's languages could not be read." /> : null}
      {copyPending && !refining ? (
        localized.status === 'error' ? (
          <>
            <HintText text="The promotion's text could not be prepared in this language." />
            <Button variant="text" label={t('Try again')} onClick={localized.retry} />
          </>
        ) : (
          <HintText text="Preparing the promotion's text in this language..." />
        )
      ) : null}
      {languageStale && !refining ? (
        <HintText text="You changed the setup. Regenerate the storyboard to match." />
      ) : null}
      <StudioSelect label={t('Template')} value={templateId} onChange={onPickTemplate} options={templateOptions} />
      <TextArea
        label={t('Prompt')}
        value={userPrompt}
        onChange={onEditPrompt}
        placeholder={t('Describe the ad you want (optional). Steers how the beats are written.')}
        rows={4}
      />
      <MoodBoardField
        images={draft.moodImages}
        pending={pendingMood}
        max={MAX_MOOD_IMAGES}
        disabled={refining || reveal !== null || active}
        onAddFiles={addMoodImages}
        onRemove={removeMoodImage}
      />
      <MascotRow>
        {styleRun.showPicker ? (
          <ArtStylePicker
            styles={styleRun.styles}
            value={styleRun.styleId}
            allowNone={styleRun.allowNone}
            onChange={styleRun.pickStyle}
            disabled={refining || reveal !== null || active}
          />
        ) : null}
        <MascotPicker
          value={mascotPick}
          onChange={setMascotPick}
          disabled={refining || reveal !== null || active}
          cast={styleRun.cast}
        />
      </MascotRow>
      <Button
        variant={draft.beats.length === 0 ? 'primary' : 'outline'}
        icon={draft.beats.length === 0 ? 'sparkle' : 'refresh'}
        label={draft.beats.length === 0 ? t('Generate storyboard') : t('Regenerate storyboard')}
        disabled={refining || reveal !== null || active || !keyArt || copyPending}
        onClick={onResetFromTemplate}
      />
    </LeftControls>
  );

  // The video-model control. On mobile it stacks under the left panel in a Toolbar; immersive folds it into
  // the compact bottom bar beside Generate (below). Template lives in the setup card. Fixed-basis so it stays
  // compact.
  const modelControlsNode = (
    <BarField>
      <StudioSelect
        label={t('Video model')}
        hideLabel
        value={videoModel}
        onChange={handleVideoModelChange}
        options={videoModelOptions}
      />
    </BarField>
  );

  // The video model this run uses (the dropdown is hidden while active, so the state is the run's model),
  // shown in parentheses after the stage so the owner knows which model is rendering.
  const modelLabel = VIDEO_MODELS.find((m) => m.id === videoModel)?.label ?? videoModel;
  // The fine-grained step the run is doing right now (`job.activity`), read ONLY while the run is in flight:
  // the field persists on the job, so a finished, failed or stopped run must never show its last step (e.g.
  // `handoff:2`). Its 2-3 phrasings cycle in the RunStatusLine; undefined (absent, unknown key, or not active)
  // falls back to the coarser stage copy. The line is keyed by the step so a new one types in immediately.
  // Branch on the pipeline BEFORE parsing: single-clip activity keys (`opening_frame`, `clip_retry`, …) and stages
  // (`prepare` / `generate` / `finishing`) are not the v1 scene-numbered / `frame-<i>` / `beat-<i>` shapes.
  const activityLines = active
    ? singleClip
      ? singleClipActivityPhrases(job?.activity, t)
      : activityPhrases(job?.activity, t)
    : undefined;
  // Before the first step the line says which wait it is: `queued` (no worker has the run yet) wins over a stale
  // activity / stage (a requeued attempt still carries the last one's, and a queued resume gets a synthesized
  // `frame-K`); a claimed run with no stage or activity yet is setting up. Keyed per state so each types in fresh.
  const runLine = (stage: string | undefined): React.ReactElement => {
    const preStart = active && (status === 'queued' || (!stage && !activityLines));
    return (
      <RunStatusLine
        key={
          preStart
            ? `prestart:${status ?? ''}`
            : activityLines
              ? `activity:${job?.activity ?? ''}`
              : `stage:${stage ?? ''}`
        }
        phrases={
          preStart
            ? preStartPhrases(status, t)
            : (activityLines ?? [singleClip ? singleClipStageCopy(stage, t) : stageCopy(stage, t)])
        }
        suffix={`(${modelLabel})`}
      />
    );
  };
  // The up-front credit cost: per beat for v1; ceil(L / 8) beat units for a single clip (spec section 3).
  const runCost = singleClip
    ? singleClipCost(perBeatCost, draft.targetDurationSeconds)
    : storyboardCost(perBeatCost, draft.beats.length);
  const statusNode = active ? (
    <StatusBlock>
      <DeployProgressTrack>
        <DeployProgressFill $pct={smoothProgress} />
      </DeployProgressTrack>
      {runLine(job?.stage)}
      <HintText
        text="This usually takes about {{minutes}} minutes. You can leave this page; the ad will be here when you come back."
        placeholders={{
          minutes: singleClip ? estimatedClipMinutes(videoModel) : estimatedMinutes(draft.beats.length, videoModel),
        }}
      />
      <Button variant="outline" icon="close" label={t('Stop generating')} onClick={stopRun} />
    </StatusBlock>
  ) : cancelled ? (
    // The owner stopped this run: a neutral notice (not a failure), and the beats stay editable so they can
    // tweak and Generate again.
    <StatusBlock>
      <HintText text="Stopped. Your credits were refunded." />
    </StatusBlock>
  ) : failed && !failedOnBeat ? (
    // Only surface a failure in the bottom bar when it is NOT pinned to a beat card (assembly / pre-loop
    // failures). A beat failure already shows its reason inline on that beat's card (via `beatError`), so
    // repeating it here is redundant clutter.
    <StatusBlock>
      {contentPolicy ? (
        <FailText
          text="The video provider blocked beat {{n}} for its content policy. Retrying will not clear it. Reword that beat on the storyboard above (remove any brand names, real people, characters or logos you don't own), then resume. Your credits were refunded."
          placeholders={{ n: failedBeat }}
        />
      ) : rejectionReason ? (
        <FailText
          text="Beat {{n}} was rejected by the video provider: {{reason}}. Adjust that beat on the storyboard above, then resume. Your credits were refunded."
          placeholders={{ n: failedBeat, reason: rejectionReason }}
        />
      ) : timedOut ? (
        <FailText
          text="Beat {{n}} took too long to generate and was stopped. Your credits were refunded. Try a shorter beat duration, then resume."
          placeholders={{ n: failedBeat }}
        />
      ) : (
        <FailText text="That storyboard ad couldn't be generated. Your credits were refunded. Please try again." />
      )}
    </StatusBlock>
  ) : resultUrl ? (
    // The finished ad now plays inside the "Finished ad" canvas node (see OutputNode); the bottom bar just
    // confirms it is ready so the owner knows the run finished even if that card is scrolled out of view.
    <StatusBlock>
      <HintText text="Your ad is ready. It's in the Finished ad card on the canvas, where you can play, download or pick a version." />
    </StatusBlock>
  ) : null;

  // Mobile stacks the footer + status under the canvas; desktop mounts them as the content of the bottom
  // floating pill (see StoryboardCanvas). Same node either way.
  const footerNode = (
    <>
      {/* While a run is in flight the Generate button is dropped entirely (Stop lives in the status block). */}
      {active ? null : (
        <Footer>
          <GenerateButton
            label={t('Generate Video')}
            cost={runCost}
            disabled={!canGenerate}
            onClick={() => void startRun()}
          />
          {matureStyle ? <MatureVideoChip /> : null}
        </Footer>
      )}
      {statusNode}
    </>
  );

  // Immersive (desktop): a single compact bottom bar (template - video model - generate), with the run
  // status / finished video strip beneath. Promotion + Regenerate live in the left panel, not here, and the
  // always-on beats/minutes summary is dropped, so the bar reads slim. Retry is per-beat on the card.
  const bottomBarNode = (
    <>
      {/* While a run is in flight, drop the model dropdown + Generate button; only the progress + Stop show. */}
      {active ? null : (
        <BottomBar>
          {modelControlsNode}
          <GenerateButton
            compact
            label={t('Generate Video')}
            cost={runCost}
            disabled={!canGenerate}
            onClick={() => void startRun()}
          />
        </BottomBar>
      )}
      {!active && matureStyle ? <MatureVideoChip /> : null}
      {statusNode}
    </>
  );

  // EASY mode: a modal (Sheet) with the setup rail + read-only beats on the left and the output player +
  // Generate on the right. Reuses this component's own setup/generate/output state, so switching to Advanced
  // (the immersive canvas) is just the other presentation of the SAME draft/run. A header toggle flips modes.
  if (mode === 'easy') {
    // The selected version's OWN video, never a fallback: a storyboard-only version (composed, not yet
    // rendered) has no url, and must NOT borrow another version's render. `resultUrl` is already merged into
    // `finishedAd.versions` as versions[0], so the selected version's url alone is the authoritative source.
    const resultVideo = finishedAd?.selectedVersion?.url;
    // The selected version is a storyboard with no render yet (distinct from "nothing generated at all").
    const selectedIsStoryboardOnly = !!finishedAd?.selectedVersion && !finishedAd.selectedVersion.url;
    // A RENDERED version is selected (has a video) and nothing is generating: the version is locked, so the
    // in-place Generate/Regenerate actions give way to an explicit "+ New version from this" branch, and the
    // model/aspect (fixed for a rendered cut) read as facts. Editing any field still forks per the lifecycle.
    const lockedView = !active && !!resultVideo;
    // Nothing composed yet for THIS pipeline (no v1 beats / no single-clip shot plan).
    const noStoryboard = singleClip ? !draft.shotPlan : draft.beats.length === 0;
    // The beats are stale when the owner has changed the setup (promotions / vibe / prompt / length, plus the
    // model for a single clip) since they were composed: dim them and lead with Regenerate. Suppressed while
    // composing or during a video run.
    const storyboardStale =
      !noStoryboard &&
      !refining &&
      !active &&
      composedSig !== null &&
      (composedSig !==
        storyboardSignature(
          draft.promotions,
          templateId,
          userPrompt,
          draft.moodImages,
          draft.targetDurationSeconds,
          draft.pipeline,
          videoModel,
          (draft.language ?? '').trim(),
        ) ||
        (composedStyle !== null && (styleRun.sendStyleId ?? '') !== composedStyle));
    // The failure reason, wrapping fully in the danger colour (no cap/scroll, unlike the immersive StatusBlock).
    // Shown INLINE under the beat that failed (below); the top notice only carries a cancel or a pre-beat failure.
    // A single clip is one clip: its failure is never a beat's and shows in the top notice (Retry = a fresh run).
    const easyFailBody = singleClip ? (
      contentPolicy ? (
        <EasyBeatError text="The video provider blocked this clip for its content policy. Edit the shot list or regenerate the storyboard, then try again. Your credits were refunded." />
      ) : rejectionReason ? (
        <EasyBeatError
          text="The video provider rejected this clip: {{reason}}. Edit the shot list or regenerate the storyboard, then try again. Your credits were refunded."
          placeholders={{ reason: rejectionReason }}
        />
      ) : timedOut ? (
        <EasyBeatError text="The clip took too long to generate and was stopped. Try again, or pick a faster model. Your credits were refunded." />
      ) : (
        <EasyBeatError text="That video ad couldn't be generated. Your credits were refunded. Please try again." />
      )
    ) : contentPolicy ? (
      <EasyBeatError
        text="Beat {{n}} was blocked by the video provider's content policy. Regenerate the storyboard, then try again. Your credits were refunded."
        placeholders={{ n: failedBeat }}
      />
    ) : rejectionReason ? (
      <EasyBeatError
        text="Beat {{n}} was rejected by the video provider: {{reason}}. Regenerate the storyboard, then try again. Your credits were refunded."
        placeholders={{ n: failedBeat, reason: rejectionReason }}
      />
    ) : timedOut ? (
      <EasyBeatError
        text="Beat {{n}} took too long to generate and was stopped. Try again, or pick a shorter target length. Your credits were refunded."
        placeholders={{ n: failedBeat }}
      />
    ) : (
      <EasyBeatError text="That video ad couldn't be generated. Your credits were refunded. Please try again." />
    );
    // A direct Retry beside the error: resumes from the failed beat (reusing the completed ones). The backend
    // now auto-softens / brand-safes a blocked beat, so a retry usually gets past what the first run tripped on.
    const easyFailReason = (
      <>
        {easyFailBody}
        <EasyRetryBtn type="button" onClick={retryRun} disabled={renderBlocked || active || submitting || !keyArt}>
          <Icon name="refresh" size={14} />
          <Text text="Retry" tag="span" />
        </EasyRetryBtn>
        {matureStyle ? <MatureVideoChip /> : null}
      </>
    );
    // The beat that failed (0-based), so its row can carry the error inline; -1 when the failure is not on a beat.
    const failedBeatIndex = failed && failedOnBeat ? failedBeat - 1 : -1;
    // Top notice ONLY for a cancel or a pre-beat (assembly) failure; a beat failure shows inline on its row.
    const easyStatus = active ? null : cancelled ? (
      <EasyNotice>
        <HintText text="Stopped. Your credits were refunded." />
      </EasyNotice>
    ) : failed && !failedOnBeat ? (
      <EasyNotice>{easyFailReason}</EasyNotice>
    ) : null;
    // The setup rail's sections and what each one says while closed. A rendered (locked) version is read-only: every
    // setup field is disabled so the ONLY way to change anything is the footer's "+ New version from this" branch
    // (`refining || reveal || active` still gates during a compose/run as before).
    const setupDisabled = refining || reveal !== null || active || lockedView;
    const toggleRailSection = (id: EasyRailSection): void =>
      setOpenRailSection((current) => (current === id ? null : id));
    const subjectNames = draft.promotions.map((p) => p.headline?.trim() || subjectChipLabel);
    const subjectSummary =
      subjectNames.length === 0
        ? subjectButtonLabel
        : subjectNames.length <= 2
          ? subjectNames.join(', ')
          : t('{{names}} +{{n}}', { names: subjectNames.slice(0, 2).join(', '), n: subjectNames.length - 2 });
    const vibeSummary = templateOptions.find((option) => option.value === templateId)?.label;
    const lengthValue = draft.targetDurationSeconds ? String(draft.targetDurationSeconds) : '';
    const lengthOption = (singleClip ? clipLengthOptions : DURATION_TARGET_OPTIONS).find(
      (option) => option.value === lengthValue,
    );
    const lengthSummary = lengthOption ? t(lengthOption.label) : t('Auto');
    const moodCount = draft.moodImages.length + pendingMood.length;
    const moodSummary = moodCount > 0 ? t('{{n}} of {{max}}', { n: moodCount, max: MAX_MOOD_IMAGES }) : t('Add images');
    return (
      <Sheet
        onClose={onClose ?? (() => undefined)}
        closeHandle={easyCloseHandle}
        ariaLabel="Video ad"
        maxWidth="1040px"
        maxHeight="100%"
        header={
          <EasyHeader>
            <EasyHeaderLeft>
              <EasyTitleWrap>
                <EasyTitleInput
                  ref={easyNameRef}
                  value={name}
                  // Size the input to its content so the pencil hugs the title instead of trailing the
                  // input's default width. Falls back to the placeholder's length while empty; shrinks
                  // (with ellipsis) via flex when the header is too narrow.
                  size={Math.max((name || t('Name this video ad')).length + 1, 4)}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('Name this video ad')}
                  aria-label={t('Video ad name')}
                />
                {/* Pencil affordance so the owner sees the title is editable; focuses the input on click. */}
                <EasyTitleEditIcon
                  type="button"
                  aria-label={t('Edit name')}
                  onClick={() => easyNameRef.current?.focus()}
                >
                  <Icon name="edit" size={14} />
                </EasyTitleEditIcon>
              </EasyTitleWrap>
              {/* The version selector governs the WHOLE ad (setup + storyboard), so it sits in the header next to
                the title rather than over the player. Shown once at least one version exists. */}
              {finishedAd && finishedAd.versions.length >= 1 && finishedAd.selectedVersionKey ? (
                <StudioSelect
                  compact
                  hideLabel
                  mobileIcon="clock"
                  label={t('Version')}
                  value={finishedAd.selectedVersionKey}
                  onChange={onSelectVersion}
                  options={finishedAd.versions.map((v, i) => ({
                    value: v.key,
                    label:
                      active && v.key === renderTargetKey && !v.url
                        ? t('Generating · {{when}}', { when: formatVersionTimestamp(v.createdAt) })
                        : !v.url
                          ? t('Storyboard, no video · {{when}}', { when: formatVersionTimestamp(v.createdAt) })
                          : i === 0
                            ? t('Latest · {{when}}', { when: formatVersionTimestamp(v.createdAt) })
                            : formatVersionTimestamp(v.createdAt),
                  }))}
                />
              ) : null}
              {/* This ad's tracking-link performance (a saved ad: its links are keyed by its id). */}
              {entityId ? <StatsPill onClick={() => statsSheet.open(entityId)} /> : null}
            </EasyHeaderLeft>
          </EasyHeader>
        }
      >
        {/* Registers --sb-glow-angle so the Generate/Regenerate button's travelling Lumio arc can animate (the
            canvas, which normally registers it, is not mounted in easy mode). */}
        <CanvasGlowAngleProperty />
        <ComposerRailTabs value={railTab} onChange={setRailTab} outputLabel="Video" />
        <EasySplit $mobileTab={railTab} $railEnter={railEnter}>
          {/* Left rail: the setup fields scroll; the storyboard action is pinned to the bottom. */}
          <EasyRail>
            <EasyRailScroll
              $locked={lockedView}
              // Clicking any locked (disabled) field falls through to here (see $locked styles) and nudges the
              // "New version from this" link, pointing the owner at the one way to make an edit. A section header
              // stays live on a locked version (browsing the setup is fine), so its clicks never nudge.
              onClick={lockedNudge.onRailClick}
            >
              {/* The setup as the Art Style page's ingredient sections (like the image banner editor's rail): one
                  open at a time, each saying what it holds while closed. */}
              <IngredientSection
                id="video-subject"
                title={subjectFieldLabel}
                icon="megaphone"
                summary={subjectSummary}
                open={openRailSection === 'subject'}
                onToggle={() => toggleRailSection('subject')}
              >
                <PromotionsField
                  hideLabel
                  label={subjectFieldLabel}
                  buttonLabel={subjectButtonLabel}
                  promotions={draft.promotions}
                  emptyChipLabel={subjectChipLabel}
                  onOpen={openPicker}
                  onRemove={onRemovePromotion}
                  removeAriaLabel={(promoName) => t('Remove {{name}}', { name: promoName })}
                  disabled={setupDisabled}
                />
              </IngredientSection>
              {/* The language every word of the ad is written in (the website's languages only). */}
              <AdLanguageSection
                id="video-language"
                open={openRailSection === 'language'}
                onToggle={() => toggleRailSection('language')}
                value={draft.language ?? ''}
                siteLanguages={site}
                onChange={onPickLanguage}
                disabled={setupDisabled}
                copyStatus={localized.status}
                translated={localized.translated}
                onRetry={localized.retry}
              />
              <IngredientSection
                id="video-vibe"
                title={t('Vibe')}
                icon="vibe"
                summary={vibeSummary}
                open={openRailSection === 'vibe'}
                onToggle={() => toggleRailSection('vibe')}
              >
                <StudioSelect
                  hideLabel
                  label={t('Vibe')}
                  value={templateId}
                  onChange={onPickTemplate}
                  options={templateOptions}
                  disabled={setupDisabled}
                />
              </IngredientSection>
              {/* Target length: v1, how many beats the storyboard gets (applied on Generate storyboard); a single
                  clip, its exact length, from the lengths the chosen model renders. */}
              <IngredientSection
                id="video-length"
                title={t('Target length')}
                icon="clock"
                summary={lengthSummary}
                open={openRailSection === 'length'}
                onToggle={() => toggleRailSection('length')}
              >
                <StudioSelect
                  hideLabel
                  label={t('Target length')}
                  value={lengthValue}
                  onChange={onPickDuration}
                  placeholder={singleClip ? undefined : t('Auto')}
                  options={
                    singleClip
                      ? clipLengthOptions
                      : // A length too short for the chosen subjects is not offered (one build-up beat each, plus the
                        // close); the current one always stays listed so an older draft still reads right.
                        DURATION_TARGET_OPTIONS.filter(
                          (o) =>
                            o.value === lengthValue ||
                            maxSubjectsForLength(Number(o.value)) >= draft.promotions.length,
                        )
                  }
                  disabled={setupDisabled}
                />
              </IngredientSection>
              <IngredientSection
                id="video-prompt"
                title={t('Prompt')}
                icon="document"
                optional
                summary={userPrompt.trim() || t('Optional')}
                open={openRailSection === 'prompt'}
                onToggle={() => toggleRailSection('prompt')}
              >
                {/* The shared TextArea's input without its label: the section header already names it. */}
                <EasyPromptInput
                  value={userPrompt}
                  rows={3}
                  aria-label={t('Prompt')}
                  placeholder={t('Describe the ad you want (optional). Steers how the beats are written.')}
                  disabled={setupDisabled}
                  onChange={(event) => onEditPrompt(event.target.value)}
                />
              </IngredientSection>
              <IngredientSection
                id="video-mood"
                title={t('Mood board')}
                icon="palette"
                summary={moodSummary}
                open={openRailSection === 'mood'}
                onToggle={() => toggleRailSection('mood')}
              >
                <SectionHint
                  text="Up to {{n}} images that set the whole video's look and feel."
                  placeholders={{ n: MAX_MOOD_IMAGES }}
                  tag="span"
                />
                {moodCount === 0 ? (
                  <SectionHint
                    text="With none added, your promotions' key visuals drive the look and feel."
                    tag="span"
                  />
                ) : null}
                <MoodBoardField
                  headless
                  images={draft.moodImages}
                  pending={pendingMood}
                  max={MAX_MOOD_IMAGES}
                  disabled={setupDisabled}
                  onAddFiles={addMoodImages}
                  onRemove={removeMoodImage}
                />
              </IngredientSection>
              {/* The style the video is drawn in (the picker shows when the brand has more than one), then which of
                  its characters appears (only while it has a cast an ad can draw). */}
              <IngredientSection
                id="video-art-style"
                title={t('Art style')}
                icon="palette"
                summary={styleRun.styleSummary}
                open={openRailSection === 'art-style'}
                onToggle={() => toggleRailSection('art-style')}
              >
                <AdArtStyleSection tenantId={tenantId} run={styleRun} disabled={setupDisabled} />
              </IngredientSection>
              {styleRun.hasCast ? (
                <IngredientSection
                  id="video-character"
                  title={t('Character')}
                  icon="user"
                  summary={styleRun.characterSummary(mascotPick)}
                  open={openRailSection === 'character'}
                  onToggle={() => toggleRailSection('character')}
                >
                  <AdCharacterSection
                    tenantId={tenantId}
                    value={mascotPick}
                    onChange={setMascotPick}
                    disabled={setupDisabled}
                    cast={styleRun.cast}
                  />
                </IngredientSection>
              ) : null}
            </EasyRailScroll>
            {/* Why the storyboard button waits: the promotions' text in the ad's language is still being prepared,
                or could not be (Try again reads it again). */}
            {copyPending && !composing && !lockedView && !active ? (
              <EasyRailNote>
                {localized.status === 'error' ? (
                  <>
                    <EasyRailNoteText text="The promotion's text could not be prepared in this language." />
                    <EasyRetryBtn type="button" onClick={localized.retry}>
                      <Icon name="refresh" size={14} />
                      <Text text="Try again" tag="span" />
                    </EasyRetryBtn>
                  </>
                ) : (
                  <EasyRailNoteText text="Preparing the promotion's text in this language..." />
                )}
              </EasyRailNote>
            ) : null}
            {/* A rendered (locked) version is read-only: the rail foot drops Regenerate and instead offers the
                single branch action, so "New version from this" sits with the (now disabled) setup fields it
                unlocks rather than across the pane by the player. While a video is actively generating there is
                nothing to regenerate, so the foot is hidden entirely (the run's progress + Stop live on the right). */}
            {lockedView ? (
              <EasyRailFoot>
                <NewVersionLink nudge={lockedNudge.nudge} onClick={() => forkIfLockedRef.current()} />
              </EasyRailFoot>
            ) : active ? null : (
              <EasyRailFoot>
                <EasyStoryboardBtn
                  type="button"
                  $primary={noStoryboard || storyboardStale}
                  $working={composing}
                  disabled={composing || active || !keyArt || copyPending}
                  onClick={() => {
                    setRailTab('output');
                    onResetFromTemplate();
                  }}
                >
                  {composing ? (
                    <EasyBtnGlyph>
                      <LumioGlyph size={17} />
                    </EasyBtnGlyph>
                  ) : noStoryboard ? (
                    <EasyBtnGlyph>
                      <LumioGlyph size={17} />
                    </EasyBtnGlyph>
                  ) : (
                    <EasyBtnGlyph>
                      <Icon name="refresh" size={18} />
                    </EasyBtnGlyph>
                  )}
                  <EasyStoryboardBtnText
                    text={
                      composing
                        ? 'Generating storyboard…'
                        : noStoryboard
                          ? 'Generate storyboard'
                          : 'Regenerate storyboard'
                    }
                  />
                </EasyStoryboardBtn>
              </EasyRailFoot>
            )}
          </EasyRail>
          {/* Right rail: the output player, status and storyboard timeline scroll; model, aspect and Generate are
              pinned to the bottom. */}
          <EasyOutput>
            <EasyOutputScroll>
              {/* A single-clip compose that failed says so (never a silent v1 fallback); the next compose clears it. */}
              {singleClip && composeError && !refining ? (
                <EasyNotice>
                  {composeError === 'unsupported' ? (
                    <EasyBeatError text="Single clip needs the latest backend. Use the Video Ads tab to keep going." />
                  ) : (
                    <EasyBeatError text="The storyboard couldn't be generated. Please try again." />
                  )}
                </EasyNotice>
              ) : null}
              {noStoryboard && !refining ? (
                // No storyboard yet (and none composing): one guided block that leads the owner through the
                // flow, instead of an idle "finished ad" placeholder + an empty beats list.
                <EasyGuide>
                  <EasyGuideIcon>
                    {/* The Lumio brand mark for this AI feature (never an approximated sparkle/star). */}
                    <LumioMark size={24} />
                  </EasyGuideIcon>
                  <EasyGuideTitle text="Build your video ad" />
                  {singleClip ? (
                    <EasyGuideText text="Set your promotion and vibe, then generate a storyboard. Your shot list previews here, and you pick a model and render the video after that." />
                  ) : (
                    <EasyGuideText text="Set your promotions and vibe, then generate a storyboard. Your beats preview here, and you pick a model and render the video after that." />
                  )}
                  <EasyGuideSteps>
                    <EasyGuideStep>
                      <EasyGuideStepNum>1</EasyGuideStepNum>
                      <EasyGuideStepText text="Choose your promotions and vibe" />
                    </EasyGuideStep>
                    <EasyGuideStep>
                      <EasyGuideStepNum>2</EasyGuideStepNum>
                      <EasyGuideStepText text="Generate the storyboard" />
                    </EasyGuideStep>
                    <EasyGuideStep>
                      <EasyGuideStepNum>3</EasyGuideStepNum>
                      <EasyGuideStepText text="Pick a model and generate the video" />
                    </EasyGuideStep>
                  </EasyGuideSteps>
                </EasyGuide>
              ) : (
                <>
                  {/* The Version selector now lives in the modal header (next to the title), so it reads as governing
                  the whole ad, not just the player below. */}
                  {/* One stage for both layers: the run preview stays the SAME element when the video arrives (laid
                  over it and dissolving while the video reveals), so the strip never remounts mid hand-over. */}
                  <EasyOutputStage>
                    {resultVideo ? (
                      <VideoReveal
                        key="video"
                        state={handOver ? (readyVideo === resultVideo ? 'run' : 'hidden') : 'off'}
                        onDone={endHandOver}
                      >
                        {/* key by url so switching versions RELOADS the element (a bare src change does not), and no
                        shared poster: each version shows its OWN first frame (the shared `finishedAd.poster` is the
                        last render's outro frame, so it made every version look identical before play). */}
                        <EasyVideo
                          key={resultVideo}
                          src={resultVideo}
                          controls
                          playsInline
                          preload="metadata"
                          $ready={readyVideo === resultVideo}
                          // Shown once the first frame decodes; also on error (a broken video must stay visible and
                          // actionable) and on suspend (iOS stops after the metadata with preload="metadata" and never
                          // fires loadeddata).
                          onLoadedData={() => setReadyVideo(resultVideo)}
                          onSuspend={() => setReadyVideo(resultVideo)}
                          onError={() => setReadyVideo(resultVideo)}
                        />
                      </VideoReveal>
                    ) : null}
                    {!resultVideo || handOver ? (
                      <EasyOutputEmpty
                        key="preview"
                        $over={!!resultVideo}
                        $dissolving={!!resultVideo && readyVideo === resultVideo}
                      >
                        {active || handOver ? <DotField seed={draft.beats.length * 7 + 3} /> : null}
                        {/* As each beat renders, frames from its clip fill its share of the preview over the dots. */}
                        {(active || handOver) && (displaySegments || generatedFrames) ? (
                          <BeatFilmstrip
                            segments={displaySegments ?? []}
                            stills={generatedFrames ?? []}
                            durations={beatDurations}
                          />
                        ) : null}
                        {handOver ? null : (
                          <EasyOutputHint
                            text={
                              active
                                ? 'Generating your ad. You can leave this page; it will be here when you come back.'
                                : selectedIsStoryboardOnly
                                  ? 'No video for this version yet. Generate it to render this storyboard.'
                                  : 'Your finished ad will play here once you generate it.'
                            }
                          />
                        )}
                      </EasyOutputEmpty>
                    ) : null}
                  </EasyOutputStage>
                  {/* Once a video exists (and no run is in flight), offer a tracking link for THIS version (minted
                  and stored by spark-creatives under the saved ad's id). */}
                  {resultVideo && !active && finishedAd?.selectedVersionKey && entityId ? (
                    <TrackingLink
                      // Key on the version so switching versions resets the hook: otherwise the panel would
                      // keep the previous version's copyable link and mis-attribute.
                      key={finishedAd.selectedVersionKey}
                      kind="video"
                      creativeId={entityId}
                      versionKey={finishedAd.selectedVersionKey}
                      promotions={draft.promotions}
                      poolItems={items}
                      brandDomain={brandDomain}
                      adName={name}
                      videoUrl={resultVideo}
                      downloadName={finishedAd.downloadName}
                      tenantId={tenantId}
                      pickDestination
                      subject={trackingSubject ? { type: subjectType, subject: trackingSubject } : undefined}
                      onBuyCredits={onBuyCredits}
                    />
                  ) : null}
                  {/* No tracking panel (an ad not saved yet): the finished mp4 must still be downloadable. */}
                  {resultVideo && !active && !(finishedAd?.selectedVersionKey && entityId) ? (
                    <Footer>
                      <Button
                        variant="outline"
                        icon="download"
                        label={t('Download video')}
                        onClick={() => void downloadAsset(resultVideo, finishedAd.downloadName)}
                      />
                    </Footer>
                  ) : null}
                  {LOCAL_COPY_REDO &&
                  devDebug &&
                  resultVideo &&
                  !active &&
                  !singleClip &&
                  !renderBlocked &&
                  job?.result_url === resultVideo ? (
                    <Footer>
                      <Button variant="outline" icon="refresh" label={t('Redraw copy')} onClick={redrawCopy} />
                    </Footer>
                  ) : null}
                  {/* Progress moves to the footer while a run is in flight (below); keep the scroll for the
                  stopped/failed notices only. */}
                  {easyStatus}
                  {/* Storyboard timeline: while Lumio composes the beats shimmer in Lumio colours, then reveal
                  staggered. During a video run the beat being rendered breathes in Lumio and the trail fills
                  in behind it (mirroring the advanced canvas). */}
                  <EasyBeatsSection>
                    {singleClip ? <EasyBeatsHead text="Shot list" /> : <EasyBeatsHead text="Storyboard" />}
                    {storyboardStale ? (
                      <EasyStaleNote text="You changed the setup. Regenerate the storyboard to match." />
                    ) : null}
                    {singleClip && draft.promotions.length > 1 ? (
                      <EasyStaleNote text="A single clip advertises one subject. Keep one, or use the Video Ads tab for more." />
                    ) : null}
                    {/* Why Generate is disabled when an owner edit blanked a required shot-list field (a length mismatch
                    already reads as the stale note above, after the setup change that caused it). */}
                    {singleClip &&
                    draft.shotPlan &&
                    !refining &&
                    shotPlanProblem(draft.shotPlan, draft.targetDurationSeconds) === 'empty' ? (
                      <EasyStaleNote text="Fill in every shot's action and camera, and the music line. Quoted text is removed, so it doesn't count." />
                    ) : null}
                    <EasyBeats $stale={storyboardStale}>
                      {refining ? (
                        Array.from({
                          length: Math.max(3, singleClip ? draft.shotPlan?.shots.length ?? 0 : draft.beats.length),
                        }).map((_, i) => (
                          <EasyBeatSkelRow key={i} $index={i}>
                            <EasyBeatSkelDot $index={i} aria-hidden="true" />
                            <EasyBeatSkelBars aria-hidden="true">
                              <EasyBeatSkelRole />
                              <EasyBeatSkelText />
                            </EasyBeatSkelBars>
                          </EasyBeatSkelRow>
                        ))
                      ) : singleClip ? (
                        // Single clip: the editable shot list (timings read-only). v1's per-beat run trail, errors and
                        // resume do not apply to one clip.
                        draft.shotPlan ? (
                          <ShotListEditor
                            plan={draft.shotPlan}
                            disabled={reveal !== null || active || lockedView}
                            onChange={onEditShotPlan}
                          />
                        ) : null
                      ) : (
                        draft.beats.map((b, i) => {
                          // Row highlight (connector fill + dimming) only during an actual run, once it reports a
                          // real (or, on a resume, synthesized) stage; until then (a queued fresh run) the timeline
                          // stays normal rather than dimming at once. `effectiveStage` fills the resume pre-stage gap.
                          const runStatus =
                            active && effectiveStage ? beatRunStatus(i, true, effectiveStage) : undefined;
                          // The dot's colour reflects generation even at rest: gray until rendered (a finished ad =>
                          // all done), Lumio while its beat renders, accent once done.
                          const dotState = runStatus ?? (resultVideo ? 'done' : 'idle');
                          return (
                            <EasyBeatRow key={i} $reveal={i} $run={runStatus}>
                              <EasyBeatDot $reveal={i} $run={dotState} aria-hidden="true" />
                              <EasyBeatText>
                                <EasyBeatRole raw text={b.role} />
                                <EasyBeatMotion raw text={b.motion_intent} />
                                {i === failedBeatIndex ? easyFailReason : null}
                              </EasyBeatText>
                              {/* No per-beat duration control in easy mode: the left-rail Target length drives the
                              beat count and the refine LLM sets each beat's length. Advanced mode keeps fine control. */}
                            </EasyBeatRow>
                          );
                        })
                      )}
                    </EasyBeats>
                    {singleClip && draft.shotPlan && !refining ? (
                      <FinalPromptPanel
                        tenantId={tenantId}
                        domain={brandDomain}
                        prompt={userPrompt.trim() || templateById(templateId).title(t)}
                        baseImageUrl={
                          draft.promotions[0] ? baseImageForAspect(draft.promotions[0], draft.aspect) : keyArt
                        }
                        aspect={draft.aspect}
                        videoModel={videoModel}
                        targetDurationSeconds={draft.targetDurationSeconds}
                        plan={draft.shotPlan}
                        promotion={draft.promotions[0]}
                        brand={{
                          name: brandName,
                          domain: brandDomain,
                          logo_url: brandLogoUrl,
                          cta_text: draft.cta || undefined,
                        }}
                        seed={compiledSeed}
                      />
                    ) : null}
                  </EasyBeatsSection>
                </>
              )}
            </EasyOutputScroll>
            {/* Model, aspect and Generate Video appear only once a storyboard exists; while a run is in
                flight they swap for the progress + Stop; with no storyboard yet the foot is hidden. Exception: a
                single clip shows it before the first compose too, because the model decides which clip lengths
                the Target length offers (Generate stays disabled until a shot plan exists). */}
            {active ? (
              <EasyRunFoot>
                <DeployProgressTrack>
                  <DeployProgressFill $pct={smoothProgress} />
                </DeployProgressTrack>
                <EasyRunFootRow>
                  {runLine(effectiveStage)}
                  <Button variant="outline" icon="close" label={t('Stop generating')} onClick={stopRun} />
                </EasyRunFootRow>
              </EasyRunFoot>
            ) : noStoryboard && !singleClip ? null : lockedView ? (
              // Rendered version: the model + aspect are fixed for this cut, so show them as read-only facts.
              // The branch action ("New version from this") lives in the left rail foot, by the fields it unlocks.
              <EasyLockedFoot>
                <EasyFootFacts>
                  <EasyFootFact raw text={modelLabel} />
                  <EasyFootFactDot aria-hidden="true">·</EasyFootFactDot>
                  {/* Just the ratio (e.g. "16:9"), not aspectLabel's "16:9 · YouTube, Facebook, X" — the foot is a terse facts row. */}
                  <EasyFootFact raw text={draft.aspect} />
                </EasyFootFacts>
              </EasyLockedFoot>
            ) : refining || reveal !== null ? null : (
              // Hidden while the storyboard composes + types in; mounts (and animates in) only once it is fully
              // ready, so the model / aspect / Generate controls arrive complete rather than sitting there disabled.
              <EasyOutputFoot>
                <EasyFootSelect
                  label="Video model"
                  value={videoModel}
                  onChange={handleVideoModelChange}
                  options={videoModelOptions}
                />
                <EasyFootSelect
                  label="Aspect ratio"
                  value={draft.aspect}
                  onChange={(v) => {
                    if (isVideoAspect(v)) handleDraftChange({ ...draft, aspect: v });
                  }}
                  options={videoAspects.map((a) => ({ value: a, label: aspectLabel(a) }))}
                />
                <GenerateButton
                  label={t('Generate Video')}
                  cost={runCost}
                  disabled={!canGenerate}
                  onClick={() => void startRun()}
                />
                {matureStyle ? <EasyFootChip /> : null}
              </EasyOutputFoot>
            )}
          </EasyOutput>
        </EasySplit>
        {pickerOpen ? subjectPickerNode : null}
        {entityId && statsSheet.value === entityId ? (
          <AssetStatsSheet kind="video" creativeId={entityId} name={name} onClose={statsSheet.close} />
        ) : null}
      </Sheet>
    );
  }

  return (
    <Root $immersive={immersed}>
      {immersed ? null : leftPanelNode}
      {immersed || active ? null : <Toolbar>{modelControlsNode}</Toolbar>}

      <StoryboardCanvas
        draft={draft}
        keyArt={keyArt}
        brandLogoUrl={brandLogoUrl}
        brandDomain={brandDomain}
        onChange={handleDraftChange}
        generatedFrames={generatedFrames}
        stage={stage}
        running={running}
        segments={displaySegments}
        staleFromBeat={staleFromBeat}
        compose={compose ?? undefined}
        videoDurations={videoDurations}
        videoAspects={videoAspects}
        failedBeat={failed && failedOnBeat ? failedBeat : undefined}
        beatError={failed && failedOnBeat ? beatError : undefined}
        immersed={immersed}
        promoPanel={immersed ? leftPanelNode : undefined}
        footer={immersed ? bottomBarNode : undefined}
        floating={immersed}
        finishedAd={finishedAd}
        onSelectVersion={onSelectVersion}
        onRegenerateFrom={regenerateFrom}
        renderBlocked={renderBlocked}
        onViewBeat={viewBeat}
      />

      {immersed ? null : footerNode}

      <BeatModal
        beatIndex={viewBeatIndex}
        beats={draft.beats}
        segments={displaySegments}
        frames={generatedFrames}
        aspect={draft.aspect}
        running={active}
        blocked={renderBlocked}
        onRegenerate={regenerateFrom}
        onClose={() => setViewBeatIndex(null)}
      />

      {pickerOpen ? subjectPickerNode : null}
    </Root>
  );
};

export default StoryboardComposer;
